import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  profileService,
  DoctorProfile,
  DoctorEducation,
  DoctorCertification,
  DoctorDocument,
  DoctorPractice,
  DoctorPracticeTiming,
  DoctorService,
  DoctorBankDetails,
  CountryCode,
  Timezone,
} from '../../services/profile/profileService';
import {
  getCurrentDoctor,
  addPractice,
  updatePractice,
  deletePractice,
  addPracticeTiming,
  getPracticeTimings,
  updatePracticeTiming,
  deletePracticeTiming,
  addService,
  updateService,
  deleteService,
  getPracticeTypes,
  getServiceCategories,
  getMedicalServicesDictionary,
  getDegreeTypes,
  getCertificationTypes,
  getSpecializations,
  getSubspecializations,
  addEducation,
  updateEducation,
  deleteEducation,
  addCertification,
  updateCertification,
  deleteCertification,
  uploadFile,
  createOrUpdateBankDetails,
  updateBankDetails,
} from '../../services/doctor-onboarding/doctorOnboardingService';
import type { ServiceCategory, MedicalService, Specialization, Subspecialization } from '../../types/doctorOnboarding';
import { authService } from '../../services/auth/authService';
import { notificationService } from '../../services/notifications/notificationService';
import { accountService } from '../../services/account/accountService';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import EditAvailabilityContent from '../../components/availability/EditAvailabilityContent/EditAvailabilityContent';
import { User, Bell, Calendar, Trash2, HelpCircle, Info, LogOut } from 'lucide-react';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar, SidebarMenuItem } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import '../dashboard/Dashboard.css';

type ProfileMenuItem =
  | 'view-profile'
  | 'notifications'
  | 'availability'
  | 'delete-account'
  | 'contact-us'
  | 'about-zivo'
  | 'logout';

type ProfileTab =
  | 'bio'
  | 'professional'
  | 'education'
  | 'certifications'
  | 'documents'
  | 'practice'
  | 'services'
  | 'bank';

const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<DoctorProfile | null>(null);
  const [education, setEducation] = useState<DoctorEducation[]>([]);
  const [certifications, setCertifications] = useState<DoctorCertification[]>([]);
  const [documents, setDocuments] = useState<DoctorDocument[]>([]);
  const [practices, setPractices] = useState<DoctorPractice[]>([]);
  const [services, setServices] = useState<DoctorService[]>([]);
  const [bankDetails, setBankDetails] = useState<DoctorBankDetails | null>(null);
  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [timezones, setTimezones] = useState<Timezone[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<ProfileMenuItem>('view-profile');
  const [activeTab, setActiveTab] = useState<ProfileTab>('bio');
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [notificationLoading, setNotificationLoading] = useState<boolean>(false);
  const [deletionStatus, setDeletionStatus] = useState<{ isScheduledForDeletion: boolean; deleteDate: string | null } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  const validMenuFromHash = (hash: string): ProfileMenuItem | null => {
    const section = hash.replace(/^#/, '').toLowerCase();
    if (['view-profile', 'notifications', 'availability', 'delete-account', 'contact-us', 'about-zivo'].includes(section)) {
      return section as ProfileMenuItem;
    }
    return null;
  };

  useEffect(() => {
    const menu = validMenuFromHash(location.hash);
    if (menu) {
      setActiveMenu(menu);
    }
  }, [location.hash]);

  // Add Practice / Add Service modals
  const [doctorId, setDoctorId] = useState<number | null>(null);
  const [showAddPracticeModal, setShowAddPracticeModal] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showTimingModal, setShowTimingModal] = useState(false);
  const [practiceForTiming, setPracticeForTiming] = useState<DoctorPractice | null>(null);
  const [editingPractice, setEditingPractice] = useState<DoctorPractice | null>(null);
  const [editingService, setEditingService] = useState<DoctorService | null>(null);
  const [existingTimingIdsByDay, setExistingTimingIdsByDay] = useState<Record<string, number>>({});
  const [practiceTypes, setPracticeTypes] = useState<Array<{ id: number; name: string; code?: string; is_active: boolean }>>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [medicalServices, setMedicalServices] = useState<MedicalService[]>([]);
  const [addPracticeLoading, setAddPracticeLoading] = useState(false);
  const [addServiceLoading, setAddServiceLoading] = useState(false);
  const [timingSaving, setTimingSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [practiceFieldErrors, setPracticeFieldErrors] = useState<Record<string, string>>({});
  const [serviceFieldErrors, setServiceFieldErrors] = useState<Record<string, string>>({});
  const [serviceEditError, setServiceEditError] = useState<string | null>(null);
  const [serviceActiveDraft, setServiceActiveDraft] = useState<boolean>(true);
  // Add Practice form
  const [practiceType, setPracticeType] = useState('clinic');
  const [practiceName, setPracticeName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postal, setPostal] = useState('');
  const [country, setCountry] = useState('India');
  const [contact, setContact] = useState('');
  const [websiteLink, setWebsiteLink] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  // Add Service form
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [serviceId, setServiceId] = useState<number | ''>('');
  const [customName, setCustomName] = useState('');
  const [fee, setFee] = useState<number | ''>('');
  const [duration, setDuration] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  // Education modal and form
  const [showAddEducationModal, setShowAddEducationModal] = useState(false);
  const [editingEducation, setEditingEducation] = useState<DoctorEducation | null>(null);
  const [degreeTypes, setDegreeTypes] = useState<Array<{ id: number; name: string; code: string; is_active: boolean }>>([]);
  const [educationDegreeType, setEducationDegreeType] = useState('');
  const [educationDegreeName, setEducationDegreeName] = useState('');
  const [educationInstitution, setEducationInstitution] = useState('');
  const [educationUniversity, setEducationUniversity] = useState('');
  const [educationYear, setEducationYear] = useState<number | ''>('');
  const [educationSpecializationId, setEducationSpecializationId] = useState<number | ''>('');
  const [educationSubspecializationId, setEducationSubspecializationId] = useState<number | ''>('');
  const [educationCertificateFile, setEducationCertificateFile] = useState<File | null>(null);
  const [educationFileInputKey, setEducationFileInputKey] = useState(0);
  const [addEducationLoading, setAddEducationLoading] = useState(false);
  const [educationModalError, setEducationModalError] = useState<string | null>(null);
  const [profileSpecializations, setProfileSpecializations] = useState<Specialization[]>([]);
  const [profileSubspecializations, setProfileSubspecializations] = useState<Subspecialization[]>([]);
  // Certification modal and form
  const [showAddCertificationModal, setShowAddCertificationModal] = useState(false);
  const [editingCertification, setEditingCertification] = useState<DoctorCertification | null>(null);
  const [certificationTypes, setCertificationTypes] = useState<Array<{ id: number; name: string; code: string; is_active: boolean }>>([]);
  const [certificationTypeId, setCertificationTypeId] = useState<number | ''>('');
  const [certificationName, setCertificationName] = useState('');
  const [certificationOrg, setCertificationOrg] = useState('');
  const [certificationIssueYear, setCertificationIssueYear] = useState<number | ''>('');
  const [certificationExpiryYear, setCertificationExpiryYear] = useState<number | ''>('');
  const [certificationNumber, setCertificationNumber] = useState('');
  const [certificationSpecializationId, setCertificationSpecializationId] = useState<number | ''>('');
  const [certificationSubspecializationId, setCertificationSubspecializationId] = useState<number | ''>('');
  const [certificationFile, setCertificationFile] = useState<File | null>(null);
  const [certificationFileInputKey, setCertificationFileInputKey] = useState(0);
  const [addCertificationLoading, setAddCertificationLoading] = useState(false);
  const [certificationModalError, setCertificationModalError] = useState<string | null>(null);
  // Profile Overview (bio + photo) edit
  const [overviewEditing, setOverviewEditing] = useState(false);
  const [overviewBioDraft, setOverviewBioDraft] = useState('');
  const [overviewPhotoFile, setOverviewPhotoFile] = useState<File | null>(null);
  const [overviewPhotoPreviewUrl, setOverviewPhotoPreviewUrl] = useState<string | null>(null);
  const [profilePhotoLoadError, setProfilePhotoLoadError] = useState(false);
  const [profilePhotoPresignedUrl, setProfilePhotoPresignedUrl] = useState<string | null>(null);
  const overviewPhotoPreviewRef = useRef<string | null>(null);
  const [overviewSaving, setOverviewSaving] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const overviewPhotoInputRef = useRef<HTMLInputElement>(null);

  // Personal information edit (email and contact number are read-only)
  const [personalEditing, setPersonalEditing] = useState(false);
  const [personalSaving, setPersonalSaving] = useState(false);
  const [personalError, setPersonalError] = useState<string | null>(null);
  const [personalDraft, setPersonalDraft] = useState<{
    title: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    date_of_birth: string;
    gender: string;
    country_code_id: number | '';
    timezone_id: number | '';
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
  }>({
    title: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    country_code_id: '',
    timezone_id: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
  });

  // Date of birth picker: calendar with Cancel/Confirm (reference-style UI)
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [dobTempDate, setDobTempDate] = useState<Date | null>(null);

  // Bank details edit (same validations as onboarding; no delete)
  const [bankEditing, setBankEditing] = useState(false);
  const [bankSaving, setBankSaving] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);
  const [bankDraft, setBankDraft] = useState<{
    account_holder_name: string;
    account_number: string;
    confirm_account_number: string;
    ifsc_code: string;
    bank_name: string;
    branch_name: string;
    account_type: 'savings' | 'current';
    upi_id: string;
    pan_number: string;
    gst_number: string;
  }>({
    account_holder_name: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    bank_name: '',
    branch_name: '',
    account_type: 'savings',
    upi_id: '',
    pan_number: '',
    gst_number: '',
  });

  // Profile Overview: photo preview object URL cleanup
  useEffect(() => {
    if (!overviewPhotoFile) {
      setOverviewPhotoPreviewUrl(null);
      if (overviewPhotoPreviewRef.current) {
        URL.revokeObjectURL(overviewPhotoPreviewRef.current);
        overviewPhotoPreviewRef.current = null;
      }
      return;
    }
    if (overviewPhotoPreviewRef.current) URL.revokeObjectURL(overviewPhotoPreviewRef.current);
    const url = URL.createObjectURL(overviewPhotoFile);
    overviewPhotoPreviewRef.current = url;
    setOverviewPhotoPreviewUrl(url);
    return () => {
      if (overviewPhotoPreviewRef.current) {
        URL.revokeObjectURL(overviewPhotoPreviewRef.current);
        overviewPhotoPreviewRef.current = null;
      }
    };
  }, [overviewPhotoFile]);

  // Timings modal
  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
  type DayConfig = { enabled: boolean; start: string; end: string };
  const [timingDays, setTimingDays] = useState<Record<string, DayConfig>>({
    monday: { enabled: false, start: '09:00', end: '17:00' },
    tuesday: { enabled: false, start: '09:00', end: '17:00' },
    wednesday: { enabled: false, start: '09:00', end: '17:00' },
    thursday: { enabled: false, start: '09:00', end: '17:00' },
    friday: { enabled: false, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' },
  });
  const [timingSlotDuration, setTimingSlotDuration] = useState(15);
  type BreakEntry = { break_start_time: string; break_end_time: string };
  const [timingBreaksByDay, setTimingBreaksByDay] = useState<Record<string, BreakEntry[]>>({});

  const timeToParts = (time: string): { hour: string; minute: string } => {
    const [h = '09', m = '00'] = time.split(':');
    return { hour: (h || '09').padStart(2, '0'), minute: (m || '00').padStart(2, '0') };
  };
  const partsToTime = (hour: string, minute: string): string =>
    `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  const updateTimingDay = useCallback((day: string, patch: Partial<DayConfig>) => {
    setTimingDays((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  }, []);

  const getBreaksForDay = (day: string): BreakEntry[] => timingBreaksByDay[day] ?? [];
  const addBreakForDay = (day: string) => {
    setTimingBreaksByDay((prev) => ({
      ...prev,
      [day]: [...(prev[day] ?? []), { break_start_time: '12:00', break_end_time: '13:00' }],
    }));
  };
  const removeBreakForDay = (day: string, index: number) => {
    setTimingBreaksByDay((prev) => {
      const list = (prev[day] ?? []).slice();
      list.splice(index, 1);
      return { ...prev, [day]: list };
    });
  };
  const updateBreakForDay = (day: string, index: number, field: 'break_start_time' | 'break_end_time', value: string) => {
    setTimingBreaksByDay((prev) => {
      const list = (prev[day] ?? []).slice();
      if (list[index]) list[index] = { ...list[index], [field]: value.slice(0, 5) };
      return { ...prev, [day]: list };
    });
  };

  const copyTimingToAllDays = (sourceDay: string) => {
    const config = timingDays[sourceDay];
    if (!config?.enabled) return;
    const start = config.start;
    const end = config.end;
    const sourceBreaks = (timingBreaksByDay[sourceDay] ?? []).map((b) => ({ ...b }));
    setTimingDays((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (prev[d].enabled) next[d] = { ...prev[d], start, end };
      });
      return next;
    });
    setTimingBreaksByDay((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (timingDays[d]?.enabled) next[d] = sourceBreaks.length ? sourceBreaks.map((b) => ({ ...b })) : [];
      });
      return next;
    });
  };

  useEffect(() => {
    const loadDeletionStatus = async () => {
      try {
        const status = await accountService.getDeletionStatus();
        setDeletionStatus({
          isScheduledForDeletion: status.isScheduledForDeletion,
          deleteDate: status.deleteDate,
        });
      } catch (err) {
        console.error('Failed to load deletion status:', err);
      }
    };

    loadDeletionStatus();
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [
          profileResult,
          educationResult,
          certificationResult,
          documentResult,
          practiceResult,
          servicesResult,
          bankResult,
        ] = await Promise.allSettled([
          profileService.getProfile(),
          profileService.getEducation(),
          profileService.getCertifications(),
          profileService.getDocuments(),
          profileService.getPractices(),
          profileService.getServices(),
          profileService.getBankDetails(),
        ]);

        // Profile
        if (profileResult.status === 'fulfilled') {
          setProfile(profileResult.value);
        } else {
          const e: any = profileResult.reason;
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;

          // If doctor profile is not created yet (404), show an empty shell instead of an error
          if (status === 404) {
            setProfile({
              id: 0,
              first_name: null,
              middle_name: null,
              last_name: null,
              full_name: null,
              email: null,
              phone_number: null,
              alternate_contact_number: null,
              date_of_birth: null,
              gender: null,
              country_code_id: null,
              timezone: null,
              timezone_id: null,
              timezone_name: null,
              profile_photo_url: null,
              bio: null,
              years_experience: null,
              registration_number: null,
              registration_council: null,
              registration_year: null,
              specialization_name: null,
              subspecialization_name: null,
              country: null,
              city: null,
              pincode: null,
              address_line1: null,
              address_line2: null,
              state: null,
              languages_spoken: null,
            });
            setError(null);
          } else {
            setError(detail || 'Failed to load profile');
          }
        }

        // Education
        if (educationResult.status === 'fulfilled') {
          setEducation(educationResult.value);
        }

        // Certifications
        if (certificationResult.status === 'fulfilled') {
          setCertifications(certificationResult.value);
        }

        // Documents
        if (documentResult.status === 'fulfilled') {
          setDocuments(documentResult.value);
        }

        // Practices
        if (practiceResult.status === 'fulfilled') {
          setPractices(practiceResult.value);
        }

        // Services
        if (servicesResult.status === 'fulfilled') {
          setServices(servicesResult.value);
        }

        // Bank details
        if (bankResult.status === 'fulfilled') {
          setBankDetails(bankResult.value);
        }

        // Load country codes and timezones separately so a 404 here doesn't block profile data
        try {
          const [countryCodesData, timezonesData] = await Promise.all([
            profileService.getCountryCodes(),
            profileService.getTimezones(),
          ]);
          setCountryCodes(countryCodesData);
          setTimezones(timezonesData);
        } catch {
          setCountryCodes([]);
          setTimezones([]);
        }
      } catch (e) {
        // Generic catch-all for unexpected failures
        // eslint-disable-next-line no-console
        console.error('Failed to load profile data', e);
        setError((prev) => prev || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, []);

  // Load notification settings and check browser permission
  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        // Ensure CSRF token is available before making requests
        await authService.ensureCsrfToken();
        
        // Check browser permission status
        const permission = notificationService.getPermission();
        setNotificationPermission(permission);

        // Load saved preference from backend
        const settings = await profileService.getNotificationSettings();
        setNotificationsEnabled(settings.notifications_enabled);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load notification settings', e);
        // Default to false if backend call fails
        setNotificationsEnabled(false);
      }
    };

    loadNotificationSettings();
  }, []);

  useEffect(() => {
    setProfilePhotoLoadError(false);
  }, [profile?.profile_photo_url]);

  // For S3-backed profile photo: fetch a presigned view URL so the image loads in the browser
  useEffect(() => {
    const uri = profile?.profile_photo_url?.trim();
    if (!uri || !uri.startsWith('s3://')) {
      setProfilePhotoPresignedUrl(null);
      return;
    }
    let cancelled = false;
    profileService
      .getPresignedViewUrl(uri)
      .then((url) => {
        if (!cancelled) setProfilePhotoPresignedUrl(url);
      })
      .catch(() => {
        if (!cancelled) setProfilePhotoPresignedUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [profile?.profile_photo_url]);

  // Debounce visibility/focus so we don't fire two PUTs (avoids duplicate CSRF 403s).
  const notificationRecheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notificationRecheckInFlightRef = useRef(false);

  // Re-check notification permission when page gains focus (e.g. user returns from Safari Settings after enabling).
  // Safari does not fire a permission change event; we must re-check on visibility/focus.
  useEffect(() => {
    const recheckPermission = async () => {
      if (!notificationService.isSupported()) return;
      if (notificationRecheckInFlightRef.current) return;
      const permission = notificationService.getPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        notificationRecheckInFlightRef.current = true;
        try {
          // Use refresh so we have a fresh CSRF token; avoid stale token → 403 on PUT.
          await authService.refreshAccessToken();
          await profileService.updateNotificationSettings(true);
          setNotificationsEnabled(true);
        } catch (e) {
          // Non-blocking: UI already shows granted
        } finally {
          notificationRecheckInFlightRef.current = false;
        }
      }
    };

    const scheduleRecheck = () => {
      if (document.visibilityState !== 'visible') return;
      if (notificationRecheckTimeoutRef.current) {
        clearTimeout(notificationRecheckTimeoutRef.current);
      }
      notificationRecheckTimeoutRef.current = setTimeout(() => {
        notificationRecheckTimeoutRef.current = null;
        recheckPermission();
      }, 150);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleRecheck();
      }
    };

    const onWindowFocus = () => {
      scheduleRecheck();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      if (notificationRecheckTimeoutRef.current) {
        clearTimeout(notificationRecheckTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, []);

  const handleNotificationToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    setNotificationLoading(true);

    try {
      // If enabling: request browser permission FIRST (no await before this).
      // Safari only shows the permission popup when triggered directly from the user gesture;
      // any await before requestPermission() can break that and the popup won't appear.
      if (enabled) {
        const permission = await notificationService.requestPermission();
        setNotificationPermission(permission);

        if (permission !== 'granted') {
          setNotificationLoading(false);
          return;
        }
      }

      const csrfToken = await authService.ensureCsrfToken();
      if (!csrfToken) {
        alert('Unable to authenticate. Please refresh the page and try again.');
        setNotificationLoading(false);
        return;
      }

      // Save preference to backend
      await profileService.updateNotificationSettings(enabled);
      setNotificationsEnabled(enabled);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to update notification settings', error);
      
      // Check if it's a CSRF error
      const errorMessage = error?.response?.data?.message || error?.response?.data?.detail || '';
      if (error?.response?.status === 403 && (
        errorMessage.includes('CSRF') || 
        errorMessage.includes('csrf') ||
        errorMessage.includes('CSRF token is required')
      )) {
        // Try to refresh CSRF token and retry once
        try {
          await authService.ensureCsrfToken();
          await profileService.updateNotificationSettings(enabled);
          setNotificationsEnabled(enabled);
          setNotificationLoading(false);
          return;
        } catch (retryError) {
          alert('Your session has expired. Please refresh the page and try again.');
        }
      } else {
        alert('Failed to update notification settings. Please try again.');
      }
      
      // Revert toggle on error
      setNotificationsEnabled(!enabled);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmationText !== 'DELETE') {
      alert('Please type DELETE to confirm account deletion.');
      return;
    }

    setDeleteLoading(true);
    try {
      // Schedule account deletion (this will deactivate the account immediately)
      const response = await accountService.scheduleDeletion();

      // Locally update deletion status based on the schedule-delete response
      setDeletionStatus({
        isScheduledForDeletion: true,
        deleteDate: response.delete_date,
      });
      setShowDeleteConfirm(false);
      setDeleteConfirmationText('');
      
      // Show success message and logout after a delay
      alert('Your account has been scheduled for deletion. You will be logged out now.');
      setTimeout(() => {
        handleLogout();
      }, 2000);
    } catch (error: any) {
      console.error('Failed to delete account:', error);
      alert(error.response?.data?.detail || error.message || 'Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleMenuClick = (item: ProfileMenuItem) => {
    if (item === 'logout') {
      handleLogout();
      return;
    }
    setActiveMenu(item);
    navigate(`/profile#${item}`, { replace: true });
  };

  const displayName =
    profile?.full_name ||
    [profile?.first_name, profile?.middle_name, profile?.last_name]
      .filter(Boolean)
      .join(' ') ||
    'Doctor';

  const sidebarItems: SidebarMenuItem[] = [
    {
      id: 'view-profile',
      label: 'Profile',
      icon: '👤',
      iconElement: <User size={20} strokeWidth={2} />,
      onClick: () => handleMenuClick('view-profile'),
      isActive: activeMenu === 'view-profile',
      isProfileCard: true,
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: '🔔',
      iconElement: <Bell size={20} strokeWidth={2} />,
      onClick: () => handleMenuClick('notifications'),
      isActive: activeMenu === 'notifications',
    },
    {
      id: 'availability',
      label: 'Edit Availability',
      icon: '📅',
      iconElement: <Calendar size={20} strokeWidth={2} />,
      onClick: () => handleMenuClick('availability'),
      isActive: activeMenu === 'availability',
    },
    {
      id: 'delete-account',
      label: 'Delete account',
      icon: '🗑️',
      iconElement: <Trash2 size={20} strokeWidth={2} />,
      onClick: () => handleMenuClick('delete-account'),
      isActive: activeMenu === 'delete-account',
    },
    {
      id: 'contact-us',
      label: 'Contact Us',
      icon: '❓',
      iconElement: <HelpCircle size={20} strokeWidth={2} />,
      onClick: () => handleMenuClick('contact-us'),
      isActive: activeMenu === 'contact-us',
      separatorBefore: true,
    },
    {
      id: 'about-zivo',
      label: 'About Zivo',
      icon: 'ℹ️',
      iconElement: <Info size={20} strokeWidth={2} />,
      onClick: () => handleMenuClick('about-zivo'),
      isActive: activeMenu === 'about-zivo',
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: '↩️',
      iconElement: <LogOut size={20} strokeWidth={2} />,
      onClick: () => handleMenuClick('logout'),
      isLogout: true,
    },
  ];

  const renderTabs = () => (
    <div className="profile-tabs">
      {[
        { id: 'bio', label: 'Profile Overview' },
        { id: 'professional', label: 'Professional Information' },
        { id: 'education', label: 'Education' },
        { id: 'certifications', label: 'Certifications' },
        { id: 'documents', label: 'Documents' },
        { id: 'practice', label: 'Practice Information' },
        { id: 'services', label: 'Services Offered' },
        { id: 'bank', label: 'Bank Details' },
      ].map((tab) => (
        <button
          key={tab.id}
          className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => setActiveTab(tab.id as ProfileTab)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    try {
      const d = new Date(value);
      return d.toLocaleDateString();
    } catch {
      return value;
    }
  };

  const formatCountryCode = (countryCodeId?: number | null) => {
    if (!countryCodeId || countryCodes.length === 0) return '';
    const match = countryCodes.find((c) => c.id === countryCodeId);
    if (!match) return '';
    // dial_code already includes the '+' prefix (e.g. '+91')
    return `${match.dial_code} (${match.country_name})`;
  };

  const formatDocumentType = (documentType: string) => {
    const map: Record<string, string> = {
      medical_license: 'Medical License',
      address_proof: 'Address Proof',
      government_id: 'Government ID',
    };
    if (map[documentType]) return map[documentType];
    // Fallback: humanize snake_case -> Title Case
    return documentType
      .split('_')
      .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ''))
      .join(' ');
  };

  const formatAccountType = (value?: string | null) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  /** Display value for email (API may return as email; show placeholder when empty) */
  const profileEmailDisplay = (profile?.email ?? '').trim() || '—';
  /** Display value for contact (API returns contact_number; we also support phone_number) */
  const profileContactDisplay = (profile?.phone_number ?? profile?.contact_number ?? '').trim() || '—';
  /** Display value for timezone: timezone_name from API, or look up by timezone_id, or placeholder */
  const profileTimezoneDisplay = (() => {
    const name = (profile?.timezone_name ?? profile?.timezone ?? '').trim();
    if (name) return name;
    if (profile?.timezone_id != null && timezones.length > 0) {
      const tz = timezones.find((t) => t.id === profile.timezone_id);
      return tz?.display_name ?? '—';
    }
    return '—';
  })();

  /** Resolve profile photo URL for img src: use presigned URL when available (S3), else prepend API base for relative paths */
  const profilePhotoSrc = (() => {
    if (profilePhotoPresignedUrl) return profilePhotoPresignedUrl;
    const url = profile?.profile_photo_url?.trim();
    if (!url) return '';
    if (url.startsWith('s3://')) return ''; // wait for presigned URL from effect
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    if (url.startsWith('/') && apiBase) return `${apiBase.replace(/\/$/, '')}${url}`;
    return url;
  })();

  const startEditOverview = () => {
    setOverviewBioDraft(profile?.bio ?? '');
    setOverviewPhotoFile(null);
    setOverviewError(null);
    setOverviewEditing(true);
  };

  const cancelEditOverview = () => {
    setOverviewEditing(false);
    setOverviewBioDraft('');
    setOverviewPhotoFile(null);
    setOverviewError(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for potential future use
  const saveOverviewBio = async () => {
    if (!profile) return;
    setOverviewSaving(true);
    setOverviewError(null);
    try {
      const updated = await profileService.updateProfile({ bio: overviewBioDraft.trim() || null });
      setProfile(updated);
      setOverviewEditing(false);
      setOverviewBioDraft('');
    } catch (e: any) {
      setOverviewError(e?.response?.data?.detail || 'Failed to save bio');
    } finally {
      setOverviewSaving(false);
    }
  };

  const handleOverviewPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setOverviewPhotoFile(file || null);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for potential future use
  const uploadAndSaveOverviewPhoto = async () => {
    if (!overviewPhotoFile || !profile) return;
    setOverviewSaving(true);
    setOverviewError(null);
    try {
      const { url } = await uploadFile(overviewPhotoFile, 'profile');
      const updated = await profileService.updateProfile({ profile_photo_url: url });
      setProfile(updated);
      setOverviewPhotoFile(null);
      if (overviewPhotoInputRef.current) overviewPhotoInputRef.current.value = '';
    } catch (e: any) {
      setOverviewError(e?.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setOverviewSaving(false);
    }
  };

  const startEditPersonal = () => {
    if (!profile) return;
    let country = profile.country ?? '';
    if (profile.country_code_id != null && countryCodes.length > 0) {
      const sel = countryCodes.find((c) => c.id === profile!.country_code_id!);
      if (sel) country = sel.country_name;
    }
    setPersonalDraft({
      title: profile.title ?? '',
      first_name: profile.first_name ?? '',
      middle_name: profile.middle_name ?? '',
      last_name: profile.last_name ?? '',
      date_of_birth: profile.date_of_birth ? String(profile.date_of_birth).slice(0, 10) : '',
      gender: (profile.gender ?? '').toLowerCase(),
      country_code_id: profile.country_code_id ?? '',
      timezone_id: profile.timezone_id ?? '',
      address_line1: profile.address_line1 ?? '',
      address_line2: profile.address_line2 ?? '',
      city: profile.city ?? '',
      state: profile.state ?? '',
      country,
      pincode: profile.pincode ?? '',
    });
    setPersonalError(null);
    setPersonalEditing(true);
  };

  const cancelEditPersonal = () => {
    setPersonalEditing(false);
    setPersonalError(null);
  };

  const startCombinedEdit = () => {
    startEditOverview();
    startEditPersonal();
  };

  const cancelCombinedEdit = () => {
    cancelEditOverview();
    cancelEditPersonal();
  };

  const saveCombined = async () => {
    if (!profile) return;
    setOverviewSaving(true);
    setPersonalSaving(true);
    setOverviewError(null);
    setPersonalError(null);
    try {
      let profile_photo_url: string | null = profile.profile_photo_url ?? null;
      if (overviewPhotoFile) {
        const { url } = await uploadFile(overviewPhotoFile, 'profile');
        profile_photo_url = url;
      }
      const payload = {
        bio: overviewBioDraft.trim() || null,
        profile_photo_url: profile_photo_url ?? undefined,
        title: personalDraft.title.trim() || undefined,
        first_name: personalDraft.first_name.trim() || undefined,
        middle_name: personalDraft.middle_name.trim() || undefined,
        last_name: personalDraft.last_name.trim() || undefined,
        date_of_birth: personalDraft.date_of_birth || undefined,
        gender: personalDraft.gender || undefined,
        timezone_id: personalDraft.timezone_id === '' ? undefined : personalDraft.timezone_id,
        address_line1: personalDraft.address_line1.trim() || undefined,
        address_line2: personalDraft.address_line2.trim() || undefined,
        city: personalDraft.city.trim() || undefined,
        state: personalDraft.state.trim() || undefined,
        country: personalDraft.country.trim() || undefined,
        pincode: personalDraft.pincode.trim() || undefined,
      };
      const updated = await profileService.updateProfile(payload);
      setProfile(updated);
      setOverviewEditing(false);
      setPersonalEditing(false);
      setOverviewBioDraft('');
      setOverviewPhotoFile(null);
      if (overviewPhotoInputRef.current) overviewPhotoInputRef.current.value = '';
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to save';
      setOverviewError(msg);
      setPersonalError(msg);
    } finally {
      setOverviewSaving(false);
      setPersonalSaving(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for potential future use
  const savePersonal = async () => {
    if (!profile) return;
    setPersonalSaving(true);
    setPersonalError(null);
    try {
      const payload = {
        title: personalDraft.title.trim() || undefined,
        first_name: personalDraft.first_name.trim() || undefined,
        middle_name: personalDraft.middle_name.trim() || undefined,
        last_name: personalDraft.last_name.trim() || undefined,
        date_of_birth: personalDraft.date_of_birth || undefined,
        gender: personalDraft.gender || undefined,
        timezone_id: personalDraft.timezone_id === '' ? undefined : personalDraft.timezone_id,
        address_line1: personalDraft.address_line1.trim() || undefined,
        address_line2: personalDraft.address_line2.trim() || undefined,
        city: personalDraft.city.trim() || undefined,
        state: personalDraft.state.trim() || undefined,
        country: personalDraft.country.trim() || undefined,
        pincode: personalDraft.pincode.trim() || undefined,
      };
      const updated = await profileService.updateProfile(payload);
      setProfile(updated);
      setPersonalEditing(false);
    } catch (e: any) {
      setPersonalError(e?.response?.data?.detail || 'Failed to save personal information');
    } finally {
      setPersonalSaving(false);
    }
  };

  const startEditBank = () => {
    if (bankDetails) {
      setBankDraft({
        account_holder_name: bankDetails.account_holder_name || '',
        account_number: '',
        confirm_account_number: '',
        ifsc_code: bankDetails.ifsc_code || '',
        bank_name: bankDetails.bank_name || '',
        branch_name: bankDetails.branch_name || '',
        account_type: (bankDetails.account_type as 'savings' | 'current') || 'savings',
        upi_id: bankDetails.upi_id || '',
        pan_number: bankDetails.pan_number || '',
        gst_number: bankDetails.gst_number || '',
      });
    } else {
      setBankDraft({
        account_holder_name: '',
        account_number: '',
        confirm_account_number: '',
        ifsc_code: '',
        bank_name: '',
        branch_name: '',
        account_type: 'savings',
        upi_id: '',
        pan_number: '',
        gst_number: '',
      });
    }
    setBankError(null);
    setBankEditing(true);
  };

  const cancelEditBank = () => {
    setBankEditing(false);
    setBankError(null);
  };

  const saveBank = async () => {
    const acctNum = bankDraft.account_number.trim();
    const confirmNum = bankDraft.confirm_account_number.trim();
    const accountHolderValid = bankDraft.account_holder_name.trim().length > 0;
    const ifscValid = bankDraft.ifsc_code.trim().length === 11;
    const accountNumValid =
      bankDetails != null
        ? acctNum === '' || (acctNum.length >= 9 && acctNum === confirmNum)
        : acctNum.length >= 9 && acctNum === confirmNum;
    if (!accountHolderValid || !ifscValid || !accountNumValid) return;

    setBankSaving(true);
    setBankError(null);
    try {
      if (bankDetails) {
        const payload: Record<string, unknown> = {
          account_holder_name: bankDraft.account_holder_name.trim(),
          ifsc_code: bankDraft.ifsc_code.trim().toUpperCase(),
          bank_name: bankDraft.bank_name.trim() || undefined,
          branch_name: bankDraft.branch_name.trim() || undefined,
          account_type: bankDraft.account_type,
          upi_id: bankDraft.upi_id.trim() || undefined,
          pan_number: bankDraft.pan_number.trim() || undefined,
          gst_number: bankDraft.gst_number.trim() || undefined,
        };
        if (acctNum) payload.account_number = acctNum;
        const updated = await updateBankDetails(payload as any);
        setBankDetails(updated as DoctorBankDetails);
        setBankEditing(false);
      } else {
        const doc = await getCurrentDoctor();
        const updated = await createOrUpdateBankDetails({
          doctor_id: doc.id,
          account_holder_name: bankDraft.account_holder_name.trim(),
          account_number: acctNum,
          ifsc_code: bankDraft.ifsc_code.trim().toUpperCase(),
          bank_name: bankDraft.bank_name.trim() || undefined,
          branch_name: bankDraft.branch_name.trim() || undefined,
          account_type: bankDraft.account_type,
          upi_id: bankDraft.upi_id.trim() || undefined,
          pan_number: bankDraft.pan_number.trim() || undefined,
          gst_number: bankDraft.gst_number.trim() || undefined,
        });
        setBankDetails(updated as DoctorBankDetails);
        setBankEditing(false);
      }
    } catch (e: any) {
      setBankError(e?.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setBankSaving(false);
    }
  };

  const bankFormValid =
    bankDraft.account_holder_name.trim().length > 0 &&
    bankDraft.ifsc_code.trim().length === 11 &&
    (bankDetails != null
      ? (bankDraft.account_number.trim() === '' &&
          bankDraft.confirm_account_number.trim() === '') ||
        (bankDraft.account_number.trim().length >= 9 &&
          bankDraft.account_number.trim() === bankDraft.confirm_account_number.trim())
      : bankDraft.account_number.trim().length >= 9 &&
        bankDraft.account_number.trim() === bankDraft.confirm_account_number.trim());

  const renderViewProfileContent = () => (
    <div className="profile-view-wrapper">
      {renderTabs()}

      {activeTab === 'bio' && profile && (
        <section className="profile-overview-single-card">
          {/* Edit button in top right corner of card */}
          <header className="profile-overview-card-header">
            {!overviewEditing && !personalEditing ? (
              <button type="button" className="btn-secondary" onClick={startCombinedEdit}>
                Edit
              </button>
            ) : (
              <div className="profile-card-header-actions">
                <button type="button" className="btn-secondary" onClick={cancelCombinedEdit} disabled={overviewSaving || personalSaving}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={saveCombined} disabled={overviewSaving || personalSaving}>
                  {overviewSaving || personalSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </header>
          {/* Hero: photo + name (inside card) */}
          <div className="profile-hero profile-hero-inside-card">
          <div className="profile-overview-photo-wrap">
              {(overviewEditing || personalEditing) ? (
                <>
                  {(overviewPhotoPreviewUrl || profilePhotoSrc) && !profilePhotoLoadError ? (
                    <img
                      src={overviewPhotoPreviewUrl || profilePhotoSrc}
                      alt={displayName}
                      className="profile-overview-photo"
                      onError={() => setProfilePhotoLoadError(true)}
                    />
                  ) : (
                    <div className="profile-overview-photo-placeholder" aria-hidden>
                      <span className="profile-overview-photo-placeholder-icon">👤</span>
                      <span className="profile-overview-photo-placeholder-text">No photo</span>
                    </div>
                  )}
                  <div className="profile-overview-photo-actions">
                    <label className="btn-secondary profile-overview-photo-btn">
                      <input
                        ref={overviewPhotoInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={handleOverviewPhotoChange}
                        className="profile-overview-photo-input"
                      />
                      Update photo
                    </label>
                    {overviewPhotoFile && <span className="profile-overview-photo-filename">{overviewPhotoFile.name}</span>}
                  </div>
                </>
              ) : (
                <>
            {profilePhotoSrc && !profilePhotoLoadError ? (
              <img
                src={profilePhotoSrc}
                alt={displayName}
                className="profile-overview-photo"
                onError={() => setProfilePhotoLoadError(true)}
              />
            ) : (
              <div className="profile-overview-photo-placeholder" aria-hidden>
                <span className="profile-overview-photo-placeholder-icon">👤</span>
                <span className="profile-overview-photo-placeholder-text">No photo</span>
              </div>
                  )}
                </>
              )}
            </div>
            <h1 className="profile-hero-name">
              {displayName}
              <span className="profile-hero-verified" aria-label="Verified" title="Verified">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#3b82f6" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </span>
            </h1>
          </div>

          {/* Bio section (inside same card) */}
          <div className="profile-overview-section profile-overview-bio-section">
            <h2 className="profile-overview-section-title">Bio</h2>
            {(overviewEditing || personalEditing) ? (
              <>
            <textarea
              id="profile-bio-textarea"
              className="profile-overview-bio-textarea"
                  value={overviewBioDraft}
                  onChange={(e) => setOverviewBioDraft(e.target.value.slice(0, 500))}
              placeholder="No bio added yet."
                  maxLength={500}
                  rows={4}
            />
                {(overviewError || personalError) && (
                  <div className="profile-overview-error" role="alert">
                    {overviewError || personalError}
          </div>
                )}
              </>
            ) : (
              <div className="bio-card-content">
                {(profile.bio ?? '').trim() ? profile.bio : 'No bio added yet.'}
              </div>
            )}
          </div>

          {/* Personal details section (in-place edit, same pattern as Bank details) */}
          <div className="profile-overview-section profile-overview-personal-section">
            <h2 className="profile-overview-section-title">Personal details</h2>
            <div className="personal-details-list personal-details-in-place">
              {!personalEditing ? (
                <div className="professional-details-two-columns">
                  <div className="professional-details-list">
                    <div className="detail-row">
                      <span className="detail-key">Title</span>
                      <span className="detail-value">{profile.title ? `${profile.title}.` : '—'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">First name</span>
                      <span className="detail-value">{profile.first_name || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Middle name</span>
                      <span className="detail-value">{profile.middle_name || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Last name</span>
                      <span className="detail-value">{profile.last_name || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Date of Birth</span>
                      <span className="detail-value">{formatDate(profile.date_of_birth)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Gender</span>
                      <span className="detail-value">{profile.gender || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Country Code</span>
                      <span className="detail-value">{formatCountryCode(profile.country_code_id)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Contact Number</span>
                      <span className="detail-value">{profileContactDisplay}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Email</span>
                      <span className="detail-value">{profileEmailDisplay}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Timezone</span>
                      <span className="detail-value">{profileTimezoneDisplay}</span>
                    </div>
                  </div>
                  <div className="professional-details-list">
                    <div className="detail-row">
                      <span className="detail-key">Address Line 1</span>
                      <span className="detail-value">{profile.address_line1 || '—'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Address Line 2</span>
                      <span className="detail-value">{profile.address_line2 || '—'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">City</span>
                      <span className="detail-value">{profile.city || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">State</span>
                      <span className="detail-value">{profile.state || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Country</span>
                      <span className="detail-value">{profile.country || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Pincode</span>
                      <span className="detail-value">{profile.pincode || ''}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="professional-details-two-columns">
                    <div className="professional-details-list">
                      <div className="detail-row">
                        <label htmlFor="profile-edit-title" className="detail-key">Title</label>
                        <select
                          id="profile-edit-title"
                          className="detail-value-input"
                          value={personalDraft.title}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, title: e.target.value }))}
                          aria-label="Title (e.g. Dr, Mr, Ms)"
                        >
                          <option value="">Select title</option>
                          <option value="Dr">Dr</option>
                          <option value="Mr">Mr</option>
                          <option value="Ms">Ms</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Prof">Prof</option>
                          <option value="Miss">Miss</option>
                        </select>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">First name</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.first_name}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, first_name: e.target.value }))}
                          placeholder="First name"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Middle name</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.middle_name}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, middle_name: e.target.value }))}
                          placeholder="Middle name"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Last name</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.last_name}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, last_name: e.target.value }))}
                          placeholder="Last name"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Date of Birth</span>
                        <div className="dob-picker-wrapper detail-value-dob">
                          {!dobPickerOpen ? (
                            <button
                              type="button"
                              className="dob-picker-trigger"
                              onClick={() => {
                                const current = personalDraft.date_of_birth
                                  ? new Date(personalDraft.date_of_birth + 'T00:00:00')
                                  : null;
                                setDobTempDate(current);
                                setDobPickerOpen(true);
                              }}
                            >
                              <span className="dob-picker-trigger-text">
                                {personalDraft.date_of_birth
                                  ? new Date(personalDraft.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', {
                                      month: 'long',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : 'Select date'}
                              </span>
                              <span className="dob-picker-trigger-icon" aria-hidden>✎</span>
                            </button>
                          ) : (
                            <div className="dob-picker-calendar">
                              <ReactDatePicker
                                inline
                                selected={dobTempDate}
                                onChange={(date: Date | null) => setDobTempDate(date)}
                                calendarClassName="dob-datepicker dob-datepicker-inline"
                                showYearDropdown
                                showMonthDropdown
                                dropdownMode="select"
                                minDate={new Date(new Date().getFullYear() - 80, new Date().getMonth(), new Date().getDate())}
                                maxDate={new Date(new Date().getFullYear() - 22, new Date().getMonth(), new Date().getDate())}
                                openToDate={
                                  dobTempDate ||
                                  (personalDraft.date_of_birth
                                    ? new Date(personalDraft.date_of_birth + 'T00:00:00')
                                    : new Date(new Date().getFullYear() - 22, new Date().getMonth(), new Date().getDate()))
                                }
                              />
                              <div className="dob-picker-footer">
                                <button
                                  type="button"
                                  className="btn-secondary dob-picker-cancel"
                                  onClick={() => setDobPickerOpen(false)}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn-primary dob-picker-confirm"
                                  onClick={() => {
                                    if (dobTempDate) {
                                      const value = new Date(
                                        dobTempDate.getTime() - dobTempDate.getTimezoneOffset() * 60000
                                      )
                                        .toISOString()
                                        .slice(0, 10);
                                      setPersonalDraft((p) => ({ ...p, date_of_birth: value }));
                                    }
                                    setDobPickerOpen(false);
                                  }}
                                >
                                  Confirm
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Gender</span>
                        <select
                          className="detail-value-input"
                          value={personalDraft.gender}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, gender: e.target.value }))}
                        >
                          <option value="">Select</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </select>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Country Code</span>
                        <span className="detail-value detail-value-readonly">{formatCountryCode(profile.country_code_id)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Contact Number</span>
                        <span className="detail-value detail-value-readonly">{profileContactDisplay}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Email</span>
                        <span className="detail-value detail-value-readonly">{profileEmailDisplay}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Timezone</span>
                        <select
                          className="detail-value-input"
                          value={personalDraft.timezone_id === '' ? '' : personalDraft.timezone_id}
                          onChange={(e) =>
                            setPersonalDraft((p) => ({
                              ...p,
                              timezone_id: e.target.value === '' ? '' : Number(e.target.value),
                            }))
                          }
                        >
                          <option value="">Select timezone</option>
                          {timezones.map((tz) => (
                            <option key={tz.id} value={tz.id}>
                              {tz.display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="professional-details-list">
                      <div className="detail-row">
                        <span className="detail-key">Address Line 1</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.address_line1}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, address_line1: e.target.value }))}
                          placeholder="Street address"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Address Line 2</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.address_line2}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, address_line2: e.target.value }))}
                          placeholder="Apartment, suite, etc."
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">City</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.city}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, city: e.target.value }))}
                          placeholder="City"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">State</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.state}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, state: e.target.value }))}
                          placeholder="State"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Country</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.country}
                          onChange={(e) => setPersonalDraft((p) => ({ ...p, country: e.target.value }))}
                          placeholder="Country"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Pincode</span>
                        <input
                          type="text"
                          className="detail-value-input"
                          value={personalDraft.pincode}
                          onChange={(e) =>
                            setPersonalDraft((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 10) }))
                          }
                          placeholder="Pincode"
                        />
                      </div>
                    </div>
                  </div>
                  {(overviewError || personalError) && (
                    <div className="profile-overview-error" role="alert" style={{ marginTop: 16 }}>
                      {overviewError || personalError}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'professional' && (
        <section className="professional-details-card">
          <header className="professional-details-header">
            <h2 className="professional-details-title">Professional credentials</h2>
          </header>
          <div className="professional-details-two-columns">
            <div className="professional-details-list">
              <div className="detail-row">
                <span className="detail-key">Registration Number</span>
                <span className="detail-value">{profile?.registration_number || ''}</span>
                </div>
              <div className="detail-row">
                <span className="detail-key">Registration Year</span>
                <span className="detail-value">{profile?.registration_year ?? ''}</span>
              </div>
              <div className="detail-row">
                <span className="detail-key">Specialization</span>
                <span className="detail-value">{profile?.specialization_name || ''}</span>
                </div>
              </div>
            <div className="professional-details-list">
              <div className="detail-row">
                <span className="detail-key">Registration Council</span>
                <span className="detail-value">{profile?.registration_council || ''}</span>
            </div>
              <div className="detail-row">
                <span className="detail-key">Years of Experience</span>
                <span className="detail-value">{profile?.years_experience ?? ''}</span>
                </div>
              <div className="detail-row">
                <span className="detail-key">Subspecialization</span>
                <span className="detail-value">{profile?.subspecialization_name || ''}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'education' && (
        <section className="profile-section-card" data-section="education">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Education</h2>
              <button
                type="button"
                className="add-button-header"
                onClick={async () => {
                  setEducationModalError(null);
                  if (doctorId == null) {
                    try {
                      const doc = await getCurrentDoctor();
                      setDoctorId(doc.id);
                    } catch (e) {
                      setEducationModalError('Failed to load. Please try again.');
                      return;
                    }
                  }
                  if (degreeTypes.length === 0) {
                    try {
                      const types = await getDegreeTypes();
                      setDegreeTypes(types);
                    } catch (e) {
                      setEducationModalError('Failed to load degree types.');
                      return;
                    }
                  }
                  setEditingEducation(null);
                  setEducationDegreeType('');
                  setEducationDegreeName('');
                  setEducationInstitution('');
                  setEducationUniversity('');
                  setEducationYear('');
                  setEducationSpecializationId('');
                  setEducationSubspecializationId('');
                  setEducationCertificateFile(null);
                  setEducationFileInputKey((k) => k + 1);
                  if (profileSpecializations.length === 0) {
                    getSpecializations().then(setProfileSpecializations);
                  }
                  setProfileSubspecializations([]);
                  setShowAddEducationModal(true);
                }}
              >
                Add education
              </button>
            </div>
          </header>
          <div className="profile-card-body">
            {education.length === 0 ? (
              <p>No education details added yet.</p>
            ) : (
              <div className="education-cards-list">
                {education.map((edu) => (
                  <div key={edu.id} className="education-entry-details">
                    <header className="profile-card-header education-sub-header">
                      <div className="profile-card-title-row">
                        <h2>{edu.degree_name} – {edu.institution_name}</h2>
                        <div className="practice-card-header-actions">
                          <button
                            type="button"
                            className="edit-button-inline"
                            onClick={async () => {
                              setEducationModalError(null);
                              if (doctorId == null) {
                                try {
                                  const doc = await getCurrentDoctor();
                                  setDoctorId(doc.id);
                                } catch (e) {
                                  setEducationModalError('Failed to load. Please try again.');
                                  return;
                                }
                              }
                              if (degreeTypes.length === 0) {
                                try {
                                  const types = await getDegreeTypes();
                                  setDegreeTypes(types);
                                } catch (e) {
                                  setEducationModalError('Failed to load degree types.');
                                  return;
                                }
                              }
                              setEditingEducation(edu);
                              setEducationDegreeType(edu.degree_type);
                              setEducationDegreeName(edu.degree_name);
                              setEducationInstitution(edu.institution_name);
                              setEducationUniversity(edu.university_name);
                              setEducationYear(edu.year_of_completion);
                              setEducationSpecializationId(edu.specialization_id ?? '');
                              setEducationSubspecializationId(edu.subspecialization_id ?? '');
                              if (profileSpecializations.length === 0) {
                                getSpecializations().then(setProfileSpecializations);
                              }
                              if (edu.specialization_id) {
                                getSubspecializations(edu.specialization_id).then(setProfileSubspecializations);
                              } else {
                                setProfileSubspecializations([]);
                              }
                              setEducationCertificateFile(null);
                              setEducationFileInputKey((k) => k + 1);
                              setShowAddEducationModal(true);
                            }}
                          >
                            Edit
                          </button>
                          {education.length > 1 && (
                            <button
                              type="button"
                              className="edit-button-inline delete-button-inline"
                              onClick={async () => {
                                if (!window.confirm(`Delete "${edu.degree_name} – ${edu.institution_name}"?`)) return;
                                setEducationModalError(null);
                                try {
                                  await deleteEducation(edu.id);
                                  const list = await profileService.getEducation();
                                  setEducation(list);
                                } catch (e: any) {
                                  setEducationModalError(e?.response?.data?.detail || 'Failed to delete education.');
                                }
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </header>
                    <div className="professional-details-two-columns">
                      <div className="professional-details-list">
                        <div className="detail-row">
                          <span className="detail-key">Degree Type</span>
                          <span className="detail-value">{edu.degree_type}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-key">Institution</span>
                          <span className="detail-value">{edu.institution_name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-key">Year of Completion</span>
                          <span className="detail-value">{edu.year_of_completion}</span>
                        </div>
                        {(edu.specialization_name || edu.subspecialization_name) && (
                          <div className="detail-row">
                            <span className="detail-key">Specialization</span>
                            <span className="detail-value">
                              {[edu.specialization_name, edu.subspecialization_name].filter(Boolean).join(' / ')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="professional-details-list">
                        <div className="detail-row">
                          <span className="detail-key">Degree Name</span>
                          <span className="detail-value">{edu.degree_name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-key">University</span>
                          <span className="detail-value">{edu.university_name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-key">Certificate</span>
                          <span className="detail-value">
                            {edu.certificate_document_url ? (
                              <button
                                type="button"
                                className="document-view-button document-view-link"
                                onClick={async () => {
                                  try {
                                    const url = await profileService.getPresignedViewUrl(edu.certificate_document_url!);
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                  } catch (e) {
                                    console.error('Failed to open certificate', e);
                                  }
                                }}
                              >
                                View certificate
                              </button>
                            ) : (
                              '—'
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'certifications' && (
        <section className="profile-section-card" data-section="certifications">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Certifications</h2>
              <button
                type="button"
                className="add-button-header"
                onClick={async () => {
                  setCertificationModalError(null);
                  if (doctorId == null) {
                    try {
                      const doc = await getCurrentDoctor();
                      setDoctorId(doc.id);
                    } catch (e) {
                      setCertificationModalError('Failed to load. Please try again.');
                      return;
                    }
                  }
                  if (certificationTypes.length === 0) {
                    try {
                      const types = await getCertificationTypes();
                      setCertificationTypes(types);
                    } catch (e) {
                      setCertificationModalError('Failed to load certification types.');
                      return;
                    }
                  }
                  setEditingCertification(null);
                  setCertificationTypeId('');
                  setCertificationName('');
                  setCertificationOrg('');
                  setCertificationIssueYear('');
                  setCertificationExpiryYear('');
                  setCertificationNumber('');
                  setCertificationSpecializationId('');
                  setCertificationSubspecializationId('');
                  setCertificationFile(null);
                  setCertificationFileInputKey((k) => k + 1);
                  if (profileSpecializations.length === 0) {
                    getSpecializations().then(setProfileSpecializations);
                  }
                  setProfileSubspecializations([]);
                  setShowAddCertificationModal(true);
                }}
              >
                Add certification
              </button>
            </div>
          </header>
          <div className="profile-card-body">
            {certifications.length === 0 ? (
              <p>No certifications added yet.</p>
            ) : (
              <div className="certifications-cards-list">
                {certifications.map((cert) => (
                  <div key={cert.id} className="certification-entry-details">
                    <header className="profile-card-header certification-sub-header">
                      <div className="profile-card-title-row">
                        <h2>{cert.certification_name}</h2>
                        <div className="practice-card-header-actions">
                          <button
                            type="button"
                            className="edit-button-inline"
                            onClick={async () => {
                              setCertificationModalError(null);
                              if (doctorId == null) {
                                try {
                                  const doc = await getCurrentDoctor();
                                  setDoctorId(doc.id);
                                } catch (e) {
                                  setCertificationModalError('Failed to load. Please try again.');
                                  return;
                                }
                              }
                              if (certificationTypes.length === 0) {
                                try {
                                  const types = await getCertificationTypes();
                                  setCertificationTypes(types);
                                } catch (e) {
                                  setCertificationModalError('Failed to load certification types.');
                                  return;
                                }
                              }
                              setEditingCertification(cert);
                              setCertificationTypeId(cert.certification_type_id ?? '');
                              setCertificationName(cert.certification_name);
                              setCertificationOrg(cert.issuing_organization);
                              setCertificationIssueYear(cert.issue_date);
                              setCertificationExpiryYear(cert.expiry_date ?? '');
                              setCertificationNumber(cert.certificate_number ?? '');
                              setCertificationSpecializationId(cert.specialization_id ?? '');
                              setCertificationSubspecializationId(cert.subspecialization_id ?? '');
                              if (profileSpecializations.length === 0) {
                                getSpecializations().then(setProfileSpecializations);
                              }
                              if (cert.specialization_id) {
                                getSubspecializations(cert.specialization_id).then(setProfileSubspecializations);
                              } else {
                                setProfileSubspecializations([]);
                              }
                              setCertificationFile(null);
                              setCertificationFileInputKey((k) => k + 1);
                              setShowAddCertificationModal(true);
                            }}
                          >
                            Edit
                          </button>
                          {certifications.length > 1 && (
                            <button
                              type="button"
                              className="edit-button-inline delete-button-inline"
                              onClick={async () => {
                                if (!window.confirm(`Delete "${cert.certification_name}"?`)) return;
                                setCertificationModalError(null);
                                try {
                                  await deleteCertification(cert.id);
                                  const list = await profileService.getCertifications();
                                  setCertifications(list);
                                } catch (e: any) {
                                  setCertificationModalError(e?.response?.data?.detail || 'Failed to delete certification.');
                                }
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </header>
                    <div className="professional-details-two-columns">
                      <div className="professional-details-list">
                        <div className="detail-row">
                          <span className="detail-key">Issuing Organization</span>
                          <span className="detail-value">{cert.issuing_organization}</span>
                        </div>
                        {(cert.specialization_name || cert.subspecialization_name) && (
                          <div className="detail-row">
                            <span className="detail-key">Specialization</span>
                            <span className="detail-value">
                              {[cert.specialization_name, cert.subspecialization_name].filter(Boolean).join(' / ')}
                            </span>
                          </div>
                        )}
                        <div className="detail-row">
                          <span className="detail-key">Issue Date</span>
                          <span className="detail-value">{cert.issue_date}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-key">Expiry Date</span>
                          <span className="detail-value">{cert.expiry_date ?? 'No expiry'}</span>
                        </div>
                      </div>
                      <div className="professional-details-list">
                        {cert.certificate_number ? (
                          <div className="detail-row">
                            <span className="detail-key">Certificate Number</span>
                            <span className="detail-value">{cert.certificate_number}</span>
                          </div>
                        ) : null}
                        <div className="detail-row">
                          <span className="detail-key">Certificate</span>
                          <span className="detail-value">
                            {cert.certificate_document_url ? (
                              <button
                                type="button"
                                className="document-view-button document-view-link"
                                onClick={async () => {
                                  try {
                                    const url = await profileService.getPresignedViewUrl(cert.certificate_document_url!);
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                  } catch (e) {
                                    console.error('Failed to open certificate', e);
                                  }
                                }}
                              >
                                View certificate
                              </button>
                            ) : (
                              '—'
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'documents' && (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Documents</h2>
            </div>
          </header>
          <div className="profile-card-body">
            {documents.length === 0 ? (
              <p>No documents uploaded yet.</p>
            ) : (
              documents.map((doc) => (
                <div key={doc.id} className="document-row">
                  <div className="document-name">{formatDocumentType(doc.document_type)}</div>
                  <button
                    type="button"
                    className="document-view-button"
                    onClick={async () => {
                      try {
                        const url = await profileService.getPresignedViewUrl(doc.document_url);
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error('Failed to open document', e);
                      }
                    }}
                  >
                    <span>View document</span>
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {activeTab === 'practice' && (
        <section className="profile-section-card" data-section="practice">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Practice information</h2>
              <button
                type="button"
                className="add-button-header"
                onClick={async () => {
                  setModalError(null);
                  if (doctorId == null) {
                    try {
                      const doc = await getCurrentDoctor();
                      setDoctorId(doc.id);
                    } catch (e) {
                      setModalError('Failed to load. Please try again.');
                      return;
                    }
                  }
                  if (practiceTypes.length === 0) {
                    try {
                      const types = await getPracticeTypes();
                      setPracticeTypes(types);
                    } catch (e) {
                      setModalError('Failed to load practice types.');
                      return;
                    }
                  }
                  setEditingPractice(null);
                  setPracticeName('');
                  setAddress1('');
                  setAddress2('');
                  setCity('');
                  setState('');
                  setPostal('');
                  setCountry('India');
                  setContact('');
                  setWebsiteLink('');
                  setContactEmail('');
                  setIsPrimary(false);
                  setShowAddPracticeModal(true);
                }}
              >
                Add practice
              </button>
            </div>
          </header>
          <div className="profile-card-body">
            {practices.length === 0 ? (
              <p>No practice locations added yet.</p>
            ) : (
              <div className="practice-cards-list">
              {practices.map((practice) => (
                <div key={practice.id} className="practice-entry-details">
                  <header className="profile-card-header practice-sub-header">
                    <div className="profile-card-title-row">
                      <h2>{practice.practice_name}{practice.is_primary ? ' (Primary)' : ''}</h2>
                      <div className="practice-card-header-actions">
                        <button
                          type="button"
                          className="edit-button-inline"
                          onClick={async () => {
                            setModalError(null);
                            if (doctorId == null) {
                              try {
                                const doc = await getCurrentDoctor();
                                setDoctorId(doc.id);
                              } catch (e) {
                                setModalError('Failed to load. Please try again.');
                                return;
                              }
                            }
                            if (practiceTypes.length === 0) {
                              try {
                                const types = await getPracticeTypes();
                                setPracticeTypes(types);
                              } catch (e) {
                                setModalError('Failed to load practice types.');
                                return;
                              }
                            }
                            setEditingPractice(practice);
                            setPracticeType(practice.practice_type);
                            setPracticeName(practice.practice_name);
                            setAddress1(practice.address_line1);
                            setAddress2(practice.address_line2 || '');
                            setCity(practice.city);
                            setState(practice.state);
                            setPostal(practice.postal_code);
                            setCountry(practice.country || 'India');
                            setContact(practice.contact_number);
                            setWebsiteLink(practice.website_link ?? '');
                            setContactEmail(practice.contact_email ?? '');
                            setIsPrimary(practice.is_primary);
                            setShowAddPracticeModal(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="edit-button-inline"
                          onClick={async () => {
                            setModalError(null);
                            setPracticeForTiming(practice);
                            try {
                              const timings = await getPracticeTimings(practice.id);
                              const dayConfig: Record<string, DayConfig> = {
                                monday: { enabled: false, start: '09:00', end: '17:00' },
                                tuesday: { enabled: false, start: '09:00', end: '17:00' },
                                wednesday: { enabled: false, start: '09:00', end: '17:00' },
                                thursday: { enabled: false, start: '09:00', end: '17:00' },
                                friday: { enabled: false, start: '09:00', end: '17:00' },
                                saturday: { enabled: false, start: '09:00', end: '17:00' },
                                sunday: { enabled: false, start: '09:00', end: '17:00' },
                              };
                              const byDay: Record<string, number> = {};
                              let slotDur = 15;
                              const breaksByDay: Record<string, BreakEntry[]> = {};
                              timings.filter((t) => t.is_available).forEach((t) => {
                                const day = (t.day_of_week || '').toLowerCase();
                                if (DAYS.includes(day)) {
                                  const startStr = typeof t.start_time === 'string' ? t.start_time : `${String((t.start_time as any)?.hour || 9).padStart(2, '0')}:${String((t.start_time as any)?.minute || 0).padStart(2, '0')}`;
                                  const endStr = typeof t.end_time === 'string' ? t.end_time : `${String((t.end_time as any)?.hour || 17).padStart(2, '0')}:${String((t.end_time as any)?.minute || 0).padStart(2, '0')}`;
                                  dayConfig[day] = { enabled: true, start: startStr.slice(0, 5), end: endStr.slice(0, 5) };
                                  byDay[day] = t.id;
                                  slotDur = t.slot_duration ?? 15;
                                  const rawBreaks = t.breaks ?? [];
                                  breaksByDay[day] = rawBreaks.map((b: { break_start_time: string; break_end_time: string }) => ({
                                    break_start_time: (typeof b.break_start_time === 'string' ? b.break_start_time : '').slice(0, 5) || '12:00',
                                    break_end_time: (typeof b.break_end_time === 'string' ? b.break_end_time : '').slice(0, 5) || '13:00',
                                  }));
                                }
                              });
                              setTimingDays(dayConfig);
                              setTimingSlotDuration(slotDur);
                              setExistingTimingIdsByDay(byDay);
                              setTimingBreaksByDay(breaksByDay);
                              setShowTimingModal(true);
                            } catch (e) {
                              setModalError('Failed to load timings.');
                            }
                          }}
                        >
                          Edit timings
                        </button>
                        {practices.length > 1 && (
                          <button
                            type="button"
                            className="edit-button-inline delete-button-inline"
                            onClick={async () => {
                              if (!window.confirm(`Delete "${practice.practice_name}"? This will also remove its timings.`)) return;
                              setModalError(null);
                              try {
                                await deletePractice(practice.id);
                                const list = await profileService.getPractices();
                                setPractices(list);
                              } catch (e: any) {
                                setModalError(e?.response?.data?.detail || 'Failed to delete practice.');
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </header>
                  <div className="professional-details-two-columns">
                    <div className="professional-details-list">
                      <div className="detail-row">
                        <span className="detail-key">Practice Type</span>
                        <span className="detail-value">{practice.practice_type}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Address Line 1</span>
                        <span className="detail-value">{practice.address_line1}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">City</span>
                        <span className="detail-value">{practice.city}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">State</span>
                        <span className="detail-value">{practice.state}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Postal Code</span>
                        <span className="detail-value">{practice.postal_code}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Primary Practice</span>
                        <span className="detail-value">{practice.is_primary ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                    <div className="professional-details-list">
                      <div className="detail-row">
                        <span className="detail-key">Country</span>
                        <span className="detail-value">{practice.country}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-key">Contact Number</span>
                        <span className="detail-value">{practice.contact_number}</span>
                      </div>
                      {(practice.website_link || practice.contact_email) && (
                        <>
                          {practice.website_link && (
                            <div className="detail-row">
                              <span className="detail-key">Website</span>
                              <span className="detail-value">
                                <a href={practice.website_link.startsWith('http') ? practice.website_link : `https://${practice.website_link}`} target="_blank" rel="noopener noreferrer">{practice.website_link}</a>
                              </span>
                            </div>
                          )}
                          {practice.contact_email && (
                            <div className="detail-row">
                              <span className="detail-key">Contact Email</span>
                              <span className="detail-value">
                                <a href={`mailto:${practice.contact_email}`}>{practice.contact_email}</a>
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {practice.timings && practice.timings.length > 0 && (
                    <div className="practice-timings">
                      <div className="practice-timings-title">Practice Timings</div>
                      {practice.timings
                        .filter((t) => t.is_available)
                        .map((timing) => (
                          <div key={timing.id} className="practice-timing-row">
                            <div className="practice-timing-day">
                              {timing.day_of_week.charAt(0).toUpperCase() +
                                timing.day_of_week.slice(1)}
                            </div>
                            <div className="practice-timing-time">
                              {`${(timing.start_time || '').slice(0, 5)} - ${(timing.end_time || '').slice(0, 5)} (${timing.slot_duration} min)`}
                              {timing.breaks && timing.breaks.length > 0 && (
                                <span className="practice-timing-breaks" style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-secondary, #555)' }}>
                                  · Breaks: {timing.breaks.map((b) => {
                                    const start = (b.break_start_time || '').slice(0, 5);
                                    const end = (b.break_end_time || '').slice(0, 5);
                                    const [sh, sm] = start.split(':').map(Number);
                                    const [eh, em] = end.split(':').map(Number);
                                    const durationMin = (eh * 60 + em) - (sh * 60 + sm);
                                    return `${start}–${end} (${durationMin} min)`;
                                  }).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'services' && (
        <section className="profile-section-card" data-section="services">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Services offered</h2>
              <button
                type="button"
                className="add-button-header"
                onClick={async () => {
                  setModalError(null);
                  if (doctorId == null) {
                    try {
                      const doc = await getCurrentDoctor();
                      setDoctorId(doc.id);
                    } catch (e) {
                      setModalError('Failed to load. Please try again.');
                      return;
                    }
                  }
                  if (serviceCategories.length === 0 || medicalServices.length === 0) {
                    try {
                      const [cats, svcs] = await Promise.all([
                        getServiceCategories(),
                        getMedicalServicesDictionary(),
                      ]);
                      setServiceCategories(cats);
                      setMedicalServices(svcs);
                    } catch (e) {
                      setModalError('Failed to load services.');
                      return;
                    }
                  }
                  setEditingService(null);
                  setCategoryId('');
                  setServiceId('');
                  setCustomName('');
                  setFee('');
                  setDuration('');
                  setDescription('');
                  setShowAddServiceModal(true);
                }}
              >
                Add service
              </button>
            </div>
          </header>
          <div className="profile-card-body">
            {services.length === 0 ? (
              <p>No services added yet.</p>
            ) : (
              <div className="services-cards-list">
                {services.map((service) => (
                  <div key={service.id} className="service-entry-details">
                    {editingService?.id === service.id ? (
                      <>
                        <header className="profile-card-header service-sub-header">
                          <div className="profile-card-title-row">
                            <h2>{service.effective_service_name || service.custom_service_name || service.service_name || 'Service'}</h2>
                            <div className="service-card-header-actions">
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => {
                                setEditingService(null);
                                setServiceEditError(null);
                                setServiceFieldErrors({});
                              }}
                              disabled={addServiceLoading}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn-primary"
                              disabled={addServiceLoading}
                              onClick={async () => {
                                const hasServiceOrCustom = serviceId !== '' || (customName.trim().length > 0);
                                const durationNum = duration === '' ? NaN : Number(duration);
                                const errors: Record<string, string> = {};
                                if (!hasServiceOrCustom) errors.serviceOrName = 'Select a service or enter a custom name.';
                                if (duration === '' || Number.isNaN(durationNum) || durationNum < 5 || durationNum > 480) {
                                  errors.duration = 'Duration is required (5–480 minutes).';
                                }
                                if (Object.keys(errors).length > 0) {
                                  setServiceFieldErrors(errors);
                                  setServiceEditError('Please fix the fields above.');
                                  return;
                                }
                                setServiceEditError(null);
                                setServiceFieldErrors({});
                                setAddServiceLoading(true);
                                try {
                                  await updateService(service.id, {
                                    service_id: serviceId === '' ? undefined : Number(serviceId),
                                    custom_service_name: serviceId === '' ? customName.trim() : undefined,
                                    service_description: description.trim() || undefined,
                                    service_fee: fee === '' ? undefined : Number(fee),
                                    duration: durationNum,
                                    is_active: serviceActiveDraft,
                                  });
                                  const refreshed = await profileService.getServices();
                                  setServices(refreshed);
                                  setEditingService(null);
                                } catch (err: any) {
                                  setServiceEditError(err?.response?.data?.detail || 'Failed to update service.');
                                } finally {
                                  setAddServiceLoading(false);
                                }
                              }}
                            >
                              {addServiceLoading ? 'Saving...' : 'Update'}
                            </button>
                            </div>
                          </div>
                        </header>
                        <div className="professional-details-two-columns">
                          <div className="professional-details-list">
                            <div className="detail-row">
                              <span className="detail-key">Category</span>
                              <select
                                className="detail-value-input"
                                value={categoryId === '' ? '' : categoryId}
                                onChange={(e) => { setCategoryId(e.target.value === '' ? '' : Number(e.target.value)); setServiceId(''); }}
                              >
                                <option value="">All categories</option>
                                {serviceCategories.map((c) => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                      </div>
                            <div className="detail-row">
                              <span className="detail-key">Service <span className="required">*</span></span>
                              <select
                                className="detail-value-input"
                                value={serviceId === '' ? '' : serviceId}
                                onChange={(e) => { setServiceId(e.target.value === '' ? '' : Number(e.target.value)); setServiceFieldErrors((p) => ({ ...p, serviceOrName: '' })); }}
                              >
                                <option value="">Select service</option>
                                {(categoryId === '' ? medicalServices : medicalServices.filter((s) => s.category_id === Number(categoryId))).map((s) => (
                                  <option key={s.id} value={s.id}>{s.service_name ?? (s as { name?: string }).name ?? 'Service'}</option>
                                ))}
                              </select>
                    </div>
                            <div className="detail-row">
                              <span className="detail-key">Custom name</span>
                              <input
                                type="text"
                                className="detail-value-input"
                                value={customName}
                                onChange={(e) => { setCustomName(e.target.value); setServiceFieldErrors((p) => ({ ...p, serviceOrName: '' })); }}
                                placeholder="Optional if you selected a service"
                              />
                        </div>
                            <div className="detail-row">
                              <span className="detail-key">Duration (min) <span className="required">*</span></span>
                              <input
                                type="number"
                                min={5}
                                max={480}
                                className="detail-value-input"
                                value={duration === '' ? '' : duration}
                                onChange={(e) => { setDuration(e.target.value === '' ? '' : Number(e.target.value)); setServiceFieldErrors((p) => ({ ...p, duration: '' })); }}
                              />
                      </div>
                            <div className="detail-row">
                              <span className="detail-key">Fee (₹)</span>
                              <input
                                type="number"
                                min={0}
                                className="detail-value-input"
                                value={fee === '' ? '' : fee}
                                onChange={(e) => setFee(e.target.value === '' ? '' : Number(e.target.value))}
                              />
                    </div>
                        </div>
                          <div className="professional-details-list">
                            <div className="detail-row">
                              <span className="detail-key">Status</span>
                              <select
                                className="detail-value-input"
                                value={serviceActiveDraft ? 'active' : 'inactive'}
                                onChange={(e) => setServiceActiveDraft(e.target.value === 'active')}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                      </div>
                            <div className="detail-row">
                              <span className="detail-key">Description</span>
                              <textarea
                                className="detail-value-input detail-value-textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Optional"
                                rows={2}
                              />
                    </div>
                        </div>
                      </div>
                        {(serviceFieldErrors.serviceOrName || serviceFieldErrors.duration) && (
                          <p className="field-hint field-hint-error" style={{ marginTop: 8 }}>
                            {serviceFieldErrors.serviceOrName || serviceFieldErrors.duration}
                          </p>
                        )}
                        {serviceEditError && (
                          <div className="profile-overview-error" role="alert" style={{ marginTop: 16 }}>{serviceEditError}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <header className="profile-card-header service-sub-header">
                          <div className="profile-card-title-row">
                            <h2>{service.effective_service_name || service.custom_service_name || service.service_name || 'Service'}</h2>
                            <div className="service-card-header-actions">
                      <button
                        type="button"
                        className="edit-button-inline"
                        onClick={async () => {
                                setServiceEditError(null);
                          setModalError(null);
                          if (doctorId == null) {
                            try {
                              const doc = await getCurrentDoctor();
                              setDoctorId(doc.id);
                            } catch (e) {
                                    setServiceEditError('Failed to load. Please try again.');
                              return;
                            }
                          }
                          let cats = serviceCategories;
                          let svcs = medicalServices;
                          if (cats.length === 0 || svcs.length === 0) {
                            try {
                              const [catsRes, svcsRes] = await Promise.all([
                                getServiceCategories(),
                                getMedicalServicesDictionary(),
                              ]);
                              cats = catsRes;
                              svcs = svcsRes;
                              setServiceCategories(cats);
                              setMedicalServices(svcs);
                            } catch (e) {
                                    setServiceEditError('Failed to load services.');
                              return;
                            }
                          }
                          setEditingService(service);
                          const svc = svcs.find((s) => s.id === service.service_id);
                          setCategoryId(svc?.category_id ?? '');
                          setServiceId(service.service_id ?? '');
                          setCustomName(service.custom_service_name || '');
                          setFee(service.service_fee ?? '');
                          setDuration(service.duration ?? '');
                          setDescription(service.service_description || '');
                                setServiceActiveDraft(service.is_active);
                        }}
                      >
                        Edit
                      </button>
                      {services.length > 1 && (
                        <button
                          type="button"
                          className="edit-button-inline delete-button-inline"
                          onClick={async () => {
                            const name = service.effective_service_name || service.custom_service_name || service.service_name || 'this service';
                            if (!window.confirm(`Delete "${name}"?`)) return;
                            setModalError(null);
                            try {
                              await deleteService(service.id);
                              const list = await profileService.getServices();
                              setServices(list);
                            } catch (e: any) {
                              setModalError(e?.response?.data?.detail || 'Failed to delete service.');
                            }
                          }}
                        >
                          Delete
                        </button>
                      )}
                            </div>
                          </div>
                        </header>
                        <div className="professional-details-two-columns">
                          <div className="professional-details-list">
                            <div className="detail-row">
                              <span className="detail-key">Duration</span>
                              <span className="detail-value">
                                {service.duration !== null && service.duration !== undefined
                                  ? `${service.duration} minutes`
                                  : ''}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-key">Service Fee</span>
                              <span className="detail-value">
                                {service.service_fee !== null && service.service_fee !== undefined
                                  ? `₹${service.service_fee.toFixed(2)}`
                                  : ''}
                              </span>
                            </div>
                          </div>
                          <div className="professional-details-list">
                            <div className="detail-row">
                              <span className="detail-key">Status</span>
                              <span className="detail-value">
                                {service.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'bank' && (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Bank details</h2>
            </div>
            {!bankEditing ? (
              <button type="button" className="btn-secondary" onClick={startEditBank}>
                {bankDetails ? 'Edit' : 'Add bank details'}
              </button>
            ) : (
              <div className="profile-card-header-actions">
                <button type="button" className="btn-secondary" onClick={cancelEditBank} disabled={bankSaving}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={saveBank} disabled={!bankFormValid || bankSaving}>
                  {bankSaving ? 'Saving...' : 'Update'}
                </button>
              </div>
            )}
          </header>
          <div className="profile-card-body">
            {!bankEditing ? (
              !bankDetails ? (
                <p>No bank details added yet. Click &quot;Add bank details&quot; to add your account information.</p>
              ) : (
                <div className="professional-details-two-columns">
                  <div className="professional-details-list">
                    <div className="detail-row">
                      <span className="detail-key">Account holder</span>
                      <span className="detail-value">{bankDetails.account_holder_name}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Account number</span>
                      <span className="detail-value">{bankDetails.masked_account_number}</span>
                  </div>
                    <div className="detail-row">
                      <span className="detail-key">Branch Name</span>
                      <span className="detail-value">{bankDetails.branch_name || ''}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">UPI ID</span>
                      <span className="detail-value">{bankDetails.upi_id || ''}</span>
                  </div>
                    <div className="detail-row">
                      <span className="detail-key">GST Number</span>
                      <span className="detail-value">{bankDetails.gst_number || ''}</span>
                </div>
                    </div>
                  <div className="professional-details-list">
                    <div className="detail-row">
                      <span className="detail-key">Bank Name</span>
                      <span className="detail-value">{bankDetails.bank_name || ''}</span>
                  </div>
                    <div className="detail-row">
                      <span className="detail-key">IFSC</span>
                      <span className="detail-value">{bankDetails.ifsc_code}</span>
                  </div>
                    <div className="detail-row">
                      <span className="detail-key">Account type</span>
                      <span className="detail-value">{formatAccountType(bankDetails.account_type)}</span>
                </div>
                    <div className="detail-row">
                      <span className="detail-key">PAN Number</span>
                      <span className="detail-value">{bankDetails.pan_number || ''}</span>
                    </div>
                  </div>
                    </div>
              )
            ) : (
              <>
                <div className="professional-details-two-columns">
                  <div className="professional-details-list">
                    <div className="detail-row">
                      <span className="detail-key">Account holder <span className="required">*</span></span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.account_holder_name}
                        onChange={(e) => setBankDraft((p) => ({ ...p, account_holder_name: e.target.value }))}
                        placeholder="Account holder name"
                      />
                  </div>
                    <div className="detail-row">
                      <span className="detail-key">Account number <span className="required">*</span></span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.account_number}
                        onChange={(e) => setBankDraft((p) => ({ ...p, account_number: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
                        placeholder={bankDetails ? 'Leave blank to keep current' : 'Account number'}
                      />
                </div>
                    <div className="detail-row">
                      <span className="detail-key">Confirm account number <span className="required">*</span></span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.confirm_account_number}
                        onChange={(e) => setBankDraft((p) => ({ ...p, confirm_account_number: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
                        placeholder={bankDetails ? 'Leave blank to keep current' : 'Confirm account number'}
                      />
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">Branch Name</span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.branch_name}
                        onChange={(e) => setBankDraft((p) => ({ ...p, branch_name: e.target.value }))}
                        placeholder="Branch name"
                      />
                  </div>
                    <div className="detail-row">
                      <span className="detail-key">UPI ID</span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.upi_id}
                        onChange={(e) => setBankDraft((p) => ({ ...p, upi_id: e.target.value }))}
                        placeholder="UPI ID (optional)"
                      />
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">GST Number</span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.gst_number}
                        onChange={(e) => setBankDraft((p) => ({ ...p, gst_number: e.target.value.toUpperCase().slice(0, 15) }))}
                        placeholder="15 characters (optional)"
                        maxLength={15}
                      />
                  </div>
                </div>
                  <div className="professional-details-list">
                    <div className="detail-row">
                      <span className="detail-key">Bank Name</span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.bank_name}
                        onChange={(e) => setBankDraft((p) => ({ ...p, bank_name: e.target.value }))}
                        placeholder="Bank name"
                      />
                    </div>
                    <div className="detail-row">
                      <span className="detail-key">IFSC <span className="required">*</span></span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.ifsc_code}
                        onChange={(e) => setBankDraft((p) => ({ ...p, ifsc_code: e.target.value.toUpperCase().slice(0, 11) }))}
                        placeholder="11 characters"
                        maxLength={11}
                      />
                  </div>
                    <div className="detail-row">
                      <span className="detail-key">Account type <span className="required">*</span></span>
                      <select
                        className="detail-value-input"
                        value={bankDraft.account_type}
                        onChange={(e) => setBankDraft((p) => ({ ...p, account_type: e.target.value as 'savings' | 'current' }))}
                      >
                        <option value="savings">Savings</option>
                        <option value="current">Current</option>
                      </select>
                </div>
                    <div className="detail-row">
                      <span className="detail-key">PAN Number</span>
                      <input
                        type="text"
                        className="detail-value-input"
                        value={bankDraft.pan_number}
                        onChange={(e) => setBankDraft((p) => ({ ...p, pan_number: e.target.value.toUpperCase().slice(0, 10) }))}
                        placeholder="10 characters (optional)"
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>
                {bankDraft.account_number && bankDraft.confirm_account_number && bankDraft.account_number !== bankDraft.confirm_account_number && (
                  <p className="field-hint" style={{ marginTop: 8 }}>Account numbers do not match</p>
                )}
                {bankError && (
                  <div className="profile-overview-error" role="alert" style={{ marginTop: 16 }}>
                    {bankError}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Add / Edit Practice modal */}
      {showAddPracticeModal && (
        <div
          className="profile-modal-overlay"
          onClick={() => { setShowAddPracticeModal(false); setEditingPractice(null); setModalError(null); setPracticeFieldErrors({}); }}
          role="presentation"
        >
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingPractice ? 'Edit practice' : 'Add practice'}</h3>
            {!editingPractice && (
              <p className="profile-modal-hint" style={{ marginBottom: 16 }}>
                You can set practice timings in the next step after saving.
              </p>
            )}
            <form
              className="profile-modal-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const trimmedName = practiceName.trim();
                const trimmedAddress1 = address1.trim();
                const trimmedCity = city.trim();
                const trimmedState = state.trim();
                const contactDigits = contact.replace(/\D/g, '');
                const postalDigits = postal.replace(/\D/g, '');
                const errors: Record<string, string> = {};
                if (!doctorId) {
                  setModalError('Session error. Please refresh and try again.');
                  return;
                }
                if (trimmedName.length < 3) errors.practiceName = 'Practice name must be at least 3 characters.';
                if (trimmedAddress1.length < 5) errors.address1 = 'Address line 1 must be at least 5 characters.';
                if (trimmedCity.length < 2) errors.city = 'City must be at least 2 characters.';
                if (trimmedState.length < 2) errors.state = 'State must be at least 2 characters.';
                if (postalDigits.length < 4) errors.postal = 'Postal code must be at least 4 digits.';
                if (contactDigits.length < 10) errors.contact = 'Contact number must be at least 10 digits.';
                const websiteTrimmed = websiteLink.trim();
                const emailTrimmed = contactEmail.trim();
                if (websiteTrimmed && !/^https?:\/\/.+/i.test(websiteTrimmed)) errors.websiteLink = 'Enter a valid URL (e.g. https://example.com).';
                if (websiteTrimmed && websiteTrimmed.length > 500) errors.websiteLink = 'Website link must not exceed 500 characters.';
                if (emailTrimmed && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailTrimmed)) errors.contactEmail = 'Enter a valid email address.';
                if (emailTrimmed && emailTrimmed.length > 255) errors.contactEmail = 'Contact email must not exceed 255 characters.';
                if (Object.keys(errors).length > 0) {
                  setPracticeFieldErrors(errors);
                  setModalError('Please fix the fields below.');
                  return;
                }
                setAddPracticeLoading(true);
                setModalError(null);
                setPracticeFieldErrors({});
                try {
                  if (editingPractice) {
                    const updated = await updatePractice(editingPractice.id, {
                      practice_type: practiceType,
                      practice_name: trimmedName,
                      address_line1: trimmedAddress1,
                      address_line2: address2.trim() || undefined,
                      city: trimmedCity,
                      state: trimmedState,
                      postal_code: postalDigits,
                      country: country.trim() || 'India',
                      contact_number: contactDigits,
                      website_link: websiteLink.trim() || undefined,
                      contact_email: contactEmail.trim() || undefined,
                      is_primary: isPrimary,
                    });
                    setPractices((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
                    setShowAddPracticeModal(false);
                    setEditingPractice(null);
                  } else {
                    const created = await addPractice({
                      doctor_id: doctorId,
                      practice_type: practiceType,
                      practice_name: trimmedName,
                      address_line1: trimmedAddress1,
                      address_line2: address2.trim() || undefined,
                      city: trimmedCity,
                      state: trimmedState,
                      postal_code: postalDigits,
                      country: country.trim() || 'India',
                      contact_number: contactDigits,
                      website_link: websiteLink.trim() || undefined,
                      contact_email: contactEmail.trim() || undefined,
                      is_primary: isPrimary,
                    });
                    setPractices((prev) => [...prev, created]);
                    setShowAddPracticeModal(false);
                    setPracticeForTiming(created);
                    setTimingDays({
                      monday: { enabled: false, start: '09:00', end: '17:00' },
                      tuesday: { enabled: false, start: '09:00', end: '17:00' },
                      wednesday: { enabled: false, start: '09:00', end: '17:00' },
                      thursday: { enabled: false, start: '09:00', end: '17:00' },
                      friday: { enabled: false, start: '09:00', end: '17:00' },
                      saturday: { enabled: false, start: '09:00', end: '17:00' },
                      sunday: { enabled: false, start: '09:00', end: '17:00' },
                    });
                    setTimingSlotDuration(15);
                    setExistingTimingIdsByDay({});
                    setShowTimingModal(true);
                  }
                } catch (err: any) {
                  setModalError(err?.response?.data?.detail || (editingPractice ? 'Failed to update practice.' : 'Failed to add practice.'));
                } finally {
                  setAddPracticeLoading(false);
                }
              }}
            >
              <div className="form-group">
                <label>Practice Type</label>
                <select value={practiceType} onChange={(e) => setPracticeType(e.target.value)}>
                  {practiceTypes.filter((p) => p.is_active).map((p) => (
                    <option key={p.id} value={(p as { code?: string }).code || p.name.toLowerCase()}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Practice Name *</label>
                <input value={practiceName} onChange={(e) => { setPracticeName(e.target.value); setPracticeFieldErrors((prev) => ({ ...prev, practiceName: '' })); }} required />
                {practiceFieldErrors.practiceName && <div className="field-hint field-hint-error">{practiceFieldErrors.practiceName}</div>}
              </div>
              <div className="form-group">
                <label>Address Line 1 *</label>
                <input value={address1} onChange={(e) => { setAddress1(e.target.value); setPracticeFieldErrors((prev) => ({ ...prev, address1: '' })); }} required />
                {practiceFieldErrors.address1 && <div className="field-hint field-hint-error">{practiceFieldErrors.address1}</div>}
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input value={address2} onChange={(e) => setAddress2(e.target.value)} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input value={city} onChange={(e) => { setCity(e.target.value); setPracticeFieldErrors((prev) => ({ ...prev, city: '' })); }} required />
                  {practiceFieldErrors.city && <div className="field-hint field-hint-error">{practiceFieldErrors.city}</div>}
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input value={state} onChange={(e) => { setState(e.target.value); setPracticeFieldErrors((prev) => ({ ...prev, state: '' })); }} required />
                  {practiceFieldErrors.state && <div className="field-hint field-hint-error">{practiceFieldErrors.state}</div>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Postal Code *</label>
                  <input value={postal} onChange={(e) => { setPostal(e.target.value.replace(/\D/g, '')); setPracticeFieldErrors((prev) => ({ ...prev, postal: '' })); }} required />
                  {practiceFieldErrors.postal && <div className="field-hint field-hint-error">{practiceFieldErrors.postal}</div>}
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input value={country} onChange={(e) => setCountry(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>Contact Number *</label>
                <input value={contact} onChange={(e) => { setContact(e.target.value.replace(/\D/g, '')); setPracticeFieldErrors((prev) => ({ ...prev, contact: '' })); }} required />
                {practiceFieldErrors.contact && <div className="field-hint field-hint-error">{practiceFieldErrors.contact}</div>}
              </div>
              <div className="form-group">
                <label>Website link</label>
                <input type="url" value={websiteLink} onChange={(e) => { setWebsiteLink(e.target.value); setPracticeFieldErrors((prev) => ({ ...prev, websiteLink: '' })); }} placeholder="https://..." />
                {practiceFieldErrors.websiteLink && <div className="field-hint field-hint-error">{practiceFieldErrors.websiteLink}</div>}
              </div>
              <div className="form-group">
                <label>Contact email address</label>
                <input type="email" value={contactEmail} onChange={(e) => { setContactEmail(e.target.value); setPracticeFieldErrors((prev) => ({ ...prev, contactEmail: '' })); }} placeholder="practice@example.com" />
                {practiceFieldErrors.contactEmail && <div className="field-hint field-hint-error">{practiceFieldErrors.contactEmail}</div>}
              </div>
              <div className="form-group">
                <label>
                  <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
                  Primary practice
                </label>
              </div>
              {modalError && <div className="onboarding-error" style={{ marginBottom: 12 }}>{modalError}</div>}
              <div className="profile-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowAddPracticeModal(false); setEditingPractice(null); setModalError(null); setPracticeFieldErrors({}); }} disabled={addPracticeLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={addPracticeLoading}>
                  {addPracticeLoading ? 'Saving...' : editingPractice ? 'Update practice' : 'Add practice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit Education modal */}
      {showAddEducationModal && (
        <div
          className="profile-modal-overlay"
          onClick={() => {
            setShowAddEducationModal(false);
            setEditingEducation(null);
            setEducationModalError(null);
          }}
          role="presentation"
        >
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingEducation ? 'Edit education' : 'Add education'}</h3>
            <form
              className="profile-modal-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const yearNum = educationYear === '' ? NaN : Number(educationYear);
                const yearValid = Number.isFinite(yearNum) && yearNum >= 1950 && yearNum <= 2030;
                if (!doctorId) {
                  setEducationModalError('Session error. Please refresh and try again.');
                  return;
                }
                if (!educationDegreeType.trim() || !educationDegreeName.trim() || !educationInstitution.trim() || !educationUniversity.trim() || !yearValid) {
                  setEducationModalError('Please fill degree type, degree name, institution, university, and a valid year (1950–2030).');
                  return;
                }
                if (!editingEducation && !educationCertificateFile) {
                  setEducationModalError('Certificate document is required for new education entries.');
                  return;
                }
                setAddEducationLoading(true);
                setEducationModalError(null);
                try {
                  let certificateUrl: string | undefined;
                  if (educationCertificateFile) {
                    const upload = await uploadFile(educationCertificateFile, 'degrees');
                    certificateUrl = upload.url;
                  }
                  if (editingEducation) {
                    await updateEducation(editingEducation.id, {
                      degree_type: educationDegreeType,
                      degree_name: educationDegreeName.trim(),
                      institution_name: educationInstitution.trim(),
                      university_name: educationUniversity.trim(),
                      year_of_completion: yearNum,
                      ...(certificateUrl ? { certificate_document_url: certificateUrl } : {}),
                      specialization_id: educationSpecializationId === '' ? undefined : educationSpecializationId,
                      subspecialization_id: educationSubspecializationId === '' ? undefined : educationSubspecializationId,
                    });
                    const list = await profileService.getEducation();
                    setEducation(list);
                    setShowAddEducationModal(false);
                    setEditingEducation(null);
                  } else if (certificateUrl) {
                    await addEducation({
                      doctor_id: doctorId,
                      degree_type: educationDegreeType,
                      degree_name: educationDegreeName.trim(),
                      institution_name: educationInstitution.trim(),
                      university_name: educationUniversity.trim(),
                      year_of_completion: yearNum,
                      certificate_document_url: certificateUrl,
                      display_order: education.length,
                      specialization_id: educationSpecializationId === '' ? undefined : educationSpecializationId,
                      subspecialization_id: educationSubspecializationId === '' ? undefined : educationSubspecializationId,
                    });
                    const list = await profileService.getEducation();
                    setEducation(list);
                    setShowAddEducationModal(false);
                    setEducationDegreeType('');
                    setEducationDegreeName('');
                    setEducationInstitution('');
                    setEducationUniversity('');
                    setEducationYear('');
                    setEducationSpecializationId('');
                    setEducationSubspecializationId('');
                    setEducationCertificateFile(null);
                    setEducationFileInputKey((k) => k + 1);
                  }
                } catch (err: any) {
                  setEducationModalError(err?.response?.data?.detail || (editingEducation ? 'Failed to update education.' : 'Failed to add education.'));
                } finally {
                  setAddEducationLoading(false);
                }
              }}
            >
              <div className="form-group">
                <label>Degree Type <span className="required">*</span></label>
                <select value={educationDegreeType} onChange={(e) => setEducationDegreeType(e.target.value)}>
                  <option value="">Select</option>
                  {degreeTypes.filter((d) => d.is_active).map((d) => (
                    <option key={d.id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Degree Name <span className="required">*</span></label>
                <input value={educationDegreeName} onChange={(e) => setEducationDegreeName(e.target.value)} placeholder="e.g. General Medicine" />
              </div>
              <div className="form-group">
                <label>Specialization (optional)</label>
                <select
                  value={educationSpecializationId === '' ? '' : educationSpecializationId}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : Number(e.target.value);
                    setEducationSpecializationId(v);
                    setEducationSubspecializationId('');
                    if (v !== '') getSubspecializations(Number(v)).then(setProfileSubspecializations);
                    else setProfileSubspecializations([]);
                  }}
                >
                  <option value="">Select</option>
                  {profileSpecializations.filter((s) => s.is_active).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Subspecialization (optional)</label>
                <select
                  value={educationSubspecializationId === '' ? '' : educationSubspecializationId}
                  onChange={(e) => setEducationSubspecializationId(e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={educationSpecializationId === ''}
                >
                  <option value="">Select</option>
                  {profileSubspecializations.filter((s) => s.is_active).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Institution Name <span className="required">*</span></label>
                <input value={educationInstitution} onChange={(e) => setEducationInstitution(e.target.value)} placeholder="Institution" />
              </div>
              <div className="form-group">
                <label>University Name <span className="required">*</span></label>
                <input value={educationUniversity} onChange={(e) => setEducationUniversity(e.target.value)} placeholder="University" />
              </div>
              <div className="form-group">
                <label>Year of Completion <span className="required">*</span></label>
                <input
                  type="number"
                  min={1950}
                  max={2030}
                  value={educationYear === '' ? '' : educationYear}
                  onChange={(e) => setEducationYear(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Year"
                />
              </div>
              <div className="form-group">
                <label>Certificate document {editingEducation ? '(optional – leave blank to keep current)' : <span className="required"> *</span>}</label>
                <input
                  key={educationFileInputKey}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setEducationCertificateFile(e.target.files?.[0] || null)}
                />
              </div>
              {educationModalError && <div className="onboarding-error" style={{ marginBottom: 12 }}>{educationModalError}</div>}
              <div className="profile-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowAddEducationModal(false); setEditingEducation(null); setEducationModalError(null); }} disabled={addEducationLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={addEducationLoading}>
                  {addEducationLoading ? 'Saving...' : editingEducation ? 'Update education' : 'Add education'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit Certification modal */}
      {showAddCertificationModal && (
        <div
          className="profile-modal-overlay"
          onClick={() => {
            setShowAddCertificationModal(false);
            setEditingCertification(null);
            setCertificationModalError(null);
          }}
          role="presentation"
        >
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingCertification ? 'Edit certification' : 'Add certification'}</h3>
            <form
              className="profile-modal-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const issueNum = certificationIssueYear === '' ? NaN : Number(certificationIssueYear);
                const expiryNum = certificationExpiryYear === '' ? undefined : Number(certificationExpiryYear);
                const issueValid = Number.isFinite(issueNum) && issueNum >= 1950 && issueNum <= 2100;
                const expiryValid = expiryNum === undefined || (Number.isFinite(expiryNum) && expiryNum >= 1950 && expiryNum <= 2100 && expiryNum >= issueNum);
                if (!doctorId) {
                  setCertificationModalError('Session error. Please refresh and try again.');
                  return;
                }
                if (certificationTypeId === '' || !certificationName.trim() || !certificationOrg.trim() || !issueValid || !expiryValid) {
                  setCertificationModalError('Please fill certification type, name, issuing organization, and a valid issue year. Expiry year must be ≥ issue year if set.');
                  return;
                }
                setAddCertificationLoading(true);
                setCertificationModalError(null);
                try {
                  let certificateUrl: string | undefined;
                  if (certificationFile) {
                    const upload = await uploadFile(certificationFile, 'certifications');
                    certificateUrl = upload.url;
                  }
                  if (editingCertification) {
                    await updateCertification(editingCertification.id, {
                      certification_type_id: Number(certificationTypeId),
                      certification_name: certificationName.trim(),
                      issuing_organization: certificationOrg.trim(),
                      issue_date: issueNum,
                      expiry_date: expiryNum,
                      certificate_number: certificationNumber.trim() || undefined,
                      ...(certificateUrl ? { certificate_document_url: certificateUrl } : {}),
                      specialization_id: certificationSpecializationId === '' ? undefined : certificationSpecializationId,
                      subspecialization_id: certificationSubspecializationId === '' ? undefined : certificationSubspecializationId,
                    });
                    const list = await profileService.getCertifications();
                    setCertifications(list);
                    setShowAddCertificationModal(false);
                    setEditingCertification(null);
                  } else {
                    await addCertification({
                      doctor_id: doctorId,
                      certification_type_id: Number(certificationTypeId),
                      certification_name: certificationName.trim(),
                      issuing_organization: certificationOrg.trim(),
                      issue_date: issueNum,
                      expiry_date: expiryNum,
                      certificate_number: certificationNumber.trim() || undefined,
                      certificate_document_url: certificateUrl,
                      specialization_id: certificationSpecializationId === '' ? undefined : certificationSpecializationId,
                      subspecialization_id: certificationSubspecializationId === '' ? undefined : certificationSubspecializationId,
                    });
                    const list = await profileService.getCertifications();
                    setCertifications(list);
                    setShowAddCertificationModal(false);
                    setCertificationTypeId('');
                    setCertificationName('');
                    setCertificationOrg('');
                    setCertificationIssueYear('');
                    setCertificationExpiryYear('');
                    setCertificationNumber('');
                    setCertificationSpecializationId('');
                    setCertificationSubspecializationId('');
                    setCertificationFile(null);
                    setCertificationFileInputKey((k) => k + 1);
                  }
                } catch (err: any) {
                  setCertificationModalError(err?.response?.data?.detail || (editingCertification ? 'Failed to update certification.' : 'Failed to add certification.'));
                } finally {
                  setAddCertificationLoading(false);
                }
              }}
            >
              <div className="form-group">
                <label>Certification Type <span className="required">*</span></label>
                <select
                  value={certificationTypeId === '' ? '' : certificationTypeId}
                  onChange={(e) => setCertificationTypeId(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <option value="">Select</option>
                  {certificationTypes.filter((c) => c.is_active).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Certification Name <span className="required">*</span></label>
                <input value={certificationName} onChange={(e) => setCertificationName(e.target.value)} placeholder="e.g. ACLS" />
              </div>
              <div className="form-group">
                <label>Specialization (optional)</label>
                <select
                  value={certificationSpecializationId === '' ? '' : certificationSpecializationId}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : Number(e.target.value);
                    setCertificationSpecializationId(v);
                    setCertificationSubspecializationId('');
                    if (v !== '') getSubspecializations(Number(v)).then(setProfileSubspecializations);
                    else setProfileSubspecializations([]);
                  }}
                >
                  <option value="">Select</option>
                  {profileSpecializations.filter((s) => s.is_active).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Subspecialization (optional)</label>
                <select
                  value={certificationSubspecializationId === '' ? '' : certificationSubspecializationId}
                  onChange={(e) => setCertificationSubspecializationId(e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={certificationSpecializationId === ''}
                >
                  <option value="">Select</option>
                  {profileSubspecializations.filter((s) => s.is_active).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Issuing Organization <span className="required">*</span></label>
                <input value={certificationOrg} onChange={(e) => setCertificationOrg(e.target.value)} placeholder="Organization" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Issue Year <span className="required">*</span></label>
                  <input
                    type="number"
                    min={1950}
                    max={2100}
                    value={certificationIssueYear === '' ? '' : certificationIssueYear}
                    onChange={(e) => setCertificationIssueYear(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="Year"
                  />
                </div>
                <div className="form-group">
                  <label>Expiry Year (optional)</label>
                  <input
                    type="number"
                    min={1950}
                    max={2100}
                    value={certificationExpiryYear === '' ? '' : certificationExpiryYear}
                    onChange={(e) => setCertificationExpiryYear(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="Year"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Certificate Number (optional)</label>
                <input value={certificationNumber} onChange={(e) => setCertificationNumber(e.target.value)} placeholder="Certificate number" />
              </div>
              <div className="form-group">
                <label>Certificate document (optional)</label>
                <input
                  key={certificationFileInputKey}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setCertificationFile(e.target.files?.[0] || null)}
                />
              </div>
              {certificationModalError && <div className="onboarding-error" style={{ marginBottom: 12 }}>{certificationModalError}</div>}
              <div className="profile-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowAddCertificationModal(false); setEditingCertification(null); setCertificationModalError(null); }} disabled={addCertificationLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={addCertificationLoading}>
                  {addCertificationLoading ? 'Saving...' : editingCertification ? 'Update certification' : 'Add certification'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Timings modal (after adding practice or editing existing) */}
      {showTimingModal && practiceForTiming && (
        <div
          className="profile-modal-overlay"
          onClick={() => { setShowTimingModal(false); setPracticeForTiming(null); setExistingTimingIdsByDay({}); setTimingBreaksByDay({}); setModalError(null); }}
          role="presentation"
        >
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{Object.keys(existingTimingIdsByDay).length > 0 ? 'Edit timings' : 'Add timings'} — {practiceForTiming.practice_name}</h3>
            <p className="onboarding-step-label" style={{ marginBottom: 16 }}>
              Select at least one day and set start/end times.
            </p>
            <div className="profile-timing-days-list">
              {DAYS.map((day) => {
                const config = timingDays[day];
                const label = day.charAt(0).toUpperCase() + day.slice(1);
                const startParts = timeToParts(config.start);
                const endParts = timeToParts(config.end);
                return (
                  <div key={day} className="profile-timing-day-row">
                    <label>
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => updateTimingDay(day, { enabled: e.target.checked })}
                      />
                      {label}
                    </label>
                    {config.enabled && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <select
                            value={startParts.hour}
                            onChange={(e) => updateTimingDay(day, { start: partsToTime(e.target.value, startParts.minute) })}
                            style={{ backgroundColor: '#fff' }}
                          >
                            {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                          <span>:</span>
                          <select
                            value={startParts.minute}
                            onChange={(e) => updateTimingDay(day, { start: partsToTime(startParts.hour, e.target.value) })}
                            style={{ backgroundColor: '#fff' }}
                          >
                            {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <span>–</span>
                          <select
                            value={endParts.hour}
                            onChange={(e) => updateTimingDay(day, { end: partsToTime(e.target.value, endParts.minute) })}
                            style={{ backgroundColor: '#fff' }}
                          >
                            {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                          <span>:</span>
                          <select
                            value={endParts.minute}
                            onChange={(e) => updateTimingDay(day, { end: partsToTime(endParts.hour, e.target.value) })}
                            style={{ backgroundColor: '#fff' }}
                          >
                            {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <button type="button" className="edit-button-inline" style={{ marginLeft: 8 }} onClick={() => copyTimingToAllDays(day)} title="Copy this day's times and breaks to other enabled days">
                            Copy to all
                          </button>
                        </div>
                        <div className="profile-timing-breaks" style={{ marginTop: 8, marginLeft: 24 }}>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary, #666)', marginBottom: 4 }}>Breaks</div>
                          {getBreaksForDay(day).map((br, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <select
                                value={timeToParts(br.break_start_time).hour}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_start_time', partsToTime(e.target.value, timeToParts(br.break_start_time).minute))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                              </select>
                              <span>:</span>
                              <select
                                value={timeToParts(br.break_start_time).minute}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_start_time', partsToTime(timeToParts(br.break_start_time).hour, e.target.value))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                              </select>
                              <span>–</span>
                              <select
                                value={timeToParts(br.break_end_time).hour}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_end_time', partsToTime(e.target.value, timeToParts(br.break_end_time).minute))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                              </select>
                              <span>:</span>
                              <select
                                value={timeToParts(br.break_end_time).minute}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_end_time', partsToTime(timeToParts(br.break_end_time).hour, e.target.value))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                              </select>
                              <button type="button" className="edit-button-inline delete-button-inline" style={{ marginLeft: 4 }} onClick={() => removeBreakForDay(day, idx)} aria-label="Remove break">Remove</button>
                            </div>
                          ))}
                          <button type="button" className="edit-button-inline" onClick={() => addBreakForDay(day)}>Add break</button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="form-group">
              <label>Slot duration (min)</label>
              <input
                type="number"
                min={5}
                max={120}
                value={timingSlotDuration}
                onChange={(e) => setTimingSlotDuration(Number(e.target.value))}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            {modalError && <div className="onboarding-error" style={{ marginBottom: 12 }}>{modalError}</div>}
            <div className="profile-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowTimingModal(false); setPracticeForTiming(null); setExistingTimingIdsByDay({}); setTimingBreaksByDay({}); setModalError(null); }} disabled={timingSaving}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={timingSaving || !DAYS.some((d) => timingDays[d].enabled)}
                onClick={async () => {
                  if (!DAYS.some((d) => timingDays[d].enabled)) {
                    setModalError('Select at least one day.');
                    return;
                  }
                  setModalError(null);
                  setTimingSaving(true);
                  try {
                    const slotDuration = Math.min(120, Math.max(1, Number(timingSlotDuration) || 15));
                    const practiceId = practiceForTiming.id;
                    for (const day of DAYS) {
                      const config = timingDays[day];
                      const existingId = existingTimingIdsByDay[day];
                      if (config.enabled) {
                        const body = {
                          day_of_week: day,
                          is_available: true,
                          start_time: config.start,
                          end_time: config.end,
                          slot_duration: slotDuration,
                          breaks: getBreaksForDay(day),
                        };
                        if (existingId) {
                          await updatePracticeTiming(practiceId, existingId, body);
                        } else {
                          await addPracticeTiming(practiceId, body as Omit<DoctorPracticeTiming, 'id' | 'practice_id' | 'created_at' | 'updated_at'>);
                        }
                      } else if (existingId) {
                        await deletePracticeTiming(practiceId, existingId);
                      }
                    }
                    const updated = await profileService.getPractices();
                    setPractices(updated);
                    setShowTimingModal(false);
                    setPracticeForTiming(null);
                    setExistingTimingIdsByDay({});
                    setTimingBreaksByDay({});
                  } catch (err: any) {
                    setModalError(err?.response?.data?.detail || 'Failed to save timings.');
                  } finally {
                    setTimingSaving(false);
                  }
                }}
              >
                {timingSaving ? 'Saving...' : 'Save timings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Service modal */}
      {showAddServiceModal && (
        <div
          className="profile-modal-overlay"
          onClick={() => { setShowAddServiceModal(false); setEditingService(null); setModalError(null); setServiceFieldErrors({}); }}
          role="presentation"
        >
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingService ? 'Edit service' : 'Add service'}</h3>
            <form
              className="profile-modal-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const hasServiceOrCustom = serviceId !== '' || (customName.trim().length > 0);
                const durationNum = duration === '' ? NaN : Number(duration);
                const errors: Record<string, string> = {};
                if (!doctorId) {
                  setModalError('Session error. Please refresh and try again.');
                  return;
                }
                if (!hasServiceOrCustom) {
                  errors.serviceOrName = 'Select a service from the list or enter a custom name.';
                }
                if (duration === '' || Number.isNaN(durationNum) || durationNum < 5 || durationNum > 480) {
                  errors.duration = 'Duration is required (5–480 minutes).';
                }
                if (Object.keys(errors).length > 0) {
                  setServiceFieldErrors(errors);
                  setModalError('Please fix the fields below.');
                  return;
                }
                setAddServiceLoading(true);
                setModalError(null);
                setServiceFieldErrors({});
                try {
                  if (editingService) {
                    await updateService(editingService.id, {
                      service_id: serviceId === '' ? undefined : Number(serviceId),
                      custom_service_name: serviceId === '' ? customName.trim() : undefined,
                      service_description: description.trim() || undefined,
                      service_fee: fee === '' ? undefined : Number(fee),
                      duration: durationNum,
                      is_active: editingService.is_active,
                    });
                    const refreshed = await profileService.getServices();
                    setServices(refreshed);
                    setShowAddServiceModal(false);
                    setEditingService(null);
                  } else {
                    await addService({
                      doctor_id: doctorId,
                      service_id: serviceId === '' ? undefined : Number(serviceId),
                      custom_service_name: serviceId === '' ? customName.trim() : undefined,
                      service_description: description.trim() || undefined,
                      service_fee: fee === '' ? undefined : Number(fee),
                      duration: durationNum,
                      display_order: services.length,
                      is_active: true,
                    });
                    const refreshed = await profileService.getServices();
                    setServices(refreshed);
                    setShowAddServiceModal(false);
                    setCategoryId('');
                    setServiceId('');
                    setCustomName('');
                    setFee('');
                    setDuration('');
                    setDescription('');
                  }
                } catch (err: any) {
                  setModalError(err?.response?.data?.detail || (editingService ? 'Failed to update service.' : 'Failed to add service.'));
                } finally {
                  setAddServiceLoading(false);
                }
              }}
            >
              <div className="form-group">
                <label>Category</label>
                <select
                  value={categoryId === '' ? '' : categoryId}
                  onChange={(e) => { setCategoryId(e.target.value === '' ? '' : Number(e.target.value)); setServiceId(''); }}
                >
                  <option value="">All categories</option>
                  {serviceCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Service</label>
                <select
                  value={serviceId === '' ? '' : serviceId}
                  onChange={(e) => { setServiceId(e.target.value === '' ? '' : Number(e.target.value)); setServiceFieldErrors((prev) => ({ ...prev, serviceOrName: '' })); }}
                >
                  <option value="">Select service</option>
                  {(categoryId === '' ? medicalServices : medicalServices.filter((s) => s.category_id === Number(categoryId))).map((s) => (
                    <option key={s.id} value={s.id}>{s.service_name ?? (s as { name?: string }).name ?? 'Service'}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Custom name (if not in list)</label>
                <input value={customName} onChange={(e) => { setCustomName(e.target.value); setServiceFieldErrors((prev) => ({ ...prev, serviceOrName: '' })); }} placeholder="Optional if you selected a service" />
                {serviceFieldErrors.serviceOrName && <div className="field-hint field-hint-error">{serviceFieldErrors.serviceOrName}</div>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fee (₹)</label>
                  <input type="number" min={0} value={fee === '' ? '' : fee} onChange={(e) => setFee(e.target.value === '' ? '' : Number(e.target.value))} />
                </div>
                <div className="form-group">
                  <label>Duration (min) *</label>
                  <input type="number" min={5} max={480} value={duration === '' ? '' : duration} onChange={(e) => { setDuration(e.target.value === '' ? '' : Number(e.target.value)); setServiceFieldErrors((prev) => ({ ...prev, duration: '' })); }} required />
                  {serviceFieldErrors.duration && <div className="field-hint field-hint-error">{serviceFieldErrors.duration}</div>}
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
              </div>
              {modalError && <div className="onboarding-error" style={{ marginBottom: 12 }}>{modalError}</div>}
              <div className="profile-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowAddServiceModal(false); setEditingService(null); setModalError(null); setServiceFieldErrors({}); }} disabled={addServiceLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={addServiceLoading}>
                  {addServiceLoading ? 'Saving...' : editingService ? 'Update service' : 'Add service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderRightPane = () => {
    if (loading) {
      return <p>Loading profile...</p>;
    }
    if (error) {
      return <p style={{ color: 'red' }}>{error}</p>;
    }
    if (!profile) {
      return <p>No profile found.</p>;
    }

    if (activeMenu === 'view-profile') {
      return (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Profession Profile</h2>
            </div>
          </header>
          <div className="profile-card-body">
            {renderViewProfileContent()}
          </div>
        </section>
      );
    }
    if (activeMenu === 'notifications') {
      return (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Notifications</h2>
            </div>
          </header>
          <div className="profile-card-body">
          <div className="notification-settings">
            {!notificationService.isSupported() ? (
              <p className="notification-error">
                Your browser does not support notifications.
              </p>
            ) : (
              <>
                <div className="notification-status">
                  <div className="notification-status-row">
                    <span className="notification-icon">🔔</span>
                    <div className="notification-status-info">
                      <div className="notification-status-title">Notifications</div>
                      {notificationPermission === 'denied' ? (
                        <div className="notification-status-message">
                          Disabled in browser settings. Please enable notifications in your browser settings to receive notifications.
                        </div>
                      ) : notificationPermission === 'granted' ? (
                        <div className="notification-status-message">
                          Enabled. You will receive notifications from ZivoHealth.
                        </div>
                      ) : (
                        <div className="notification-status-message">
                          Click the toggle below to enable notifications.
                        </div>
                      )}
                    </div>
                    <label
                      className={`notification-toggle ${
                        notificationsEnabled && notificationPermission === 'granted'
                          ? 'notification-toggle--on'
                          : 'notification-toggle--off'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={notificationsEnabled && notificationPermission === 'granted'}
                        disabled={notificationPermission === 'denied' || notificationLoading}
                        onChange={handleNotificationToggle}
                      />
                      <span className="notification-toggle-slider"></span>
                    </label>
                  </div>
                </div>
                {notificationPermission === 'denied' && (
                  <div className="notification-help">
                    <p>
                      <strong>How to enable notifications:</strong>
                    </p>
                    <ul>
                      <li>Click the lock icon in your browser's address bar</li>
                      <li>Find "Notifications" in the permissions list</li>
                      <li>Change it from "Block" to "Allow"</li>
                      <li>Refresh this page</li>
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
          </div>
        </section>
      );
    }
    if (activeMenu === 'availability') {
      return (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Edit Availability</h2>
            </div>
          </header>
          <div className="profile-card-body">
            <EditAvailabilityContent practices={practices} />
          </div>
        </section>
      );
    }
    if (activeMenu === 'delete-account') {
      return (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Delete Account</h2>
            </div>
          </header>
          <div className="profile-card-body">
          {deletionStatus?.isScheduledForDeletion ? (
            <div className="delete-account-scheduled">
              <div className="delete-account-warning">
                <strong>⚠️ Your account is scheduled for deletion</strong>
                <p>
                  Your account has been deactivated and is scheduled for deletion on{' '}
                  {deletionStatus.deleteDate
                    ? new Date(deletionStatus.deleteDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'a future date'}
                  .
                </p>
                <p>
                  Once deleted, all your data will be permanently removed and cannot be recovered.
                </p>
              </div>
            </div>
          ) : (
            <div className="delete-account-content">
              <div className="delete-account-warning">
                <strong>⚠️ Warning: This action cannot be undone</strong>
                <p>
                  Deleting your account will permanently remove all your data, including:
                </p>
                <ul>
                  <li>Your profile and personal information</li>
                  <li>All appointments and consultation history</li>
                  <li>Practice information and availability settings</li>
                  <li>Education, certifications, and documents</li>
                  <li>All other account-related data</li>
                </ul>
                <p>
                  <strong>Your account will be deactivated immediately.</strong> Account deletion can
                  be revoked within 7 days by logging back into the app. After 7 days, all data will
                  be permanently deleted from our servers.
                </p>
              </div>
              <button
                className="delete-account-button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Processing...' : 'Delete My Account'}
              </button>
            </div>
          )}

          {/* Confirmation Modal */}
          {showDeleteConfirm && (
            <div
              className="modal-overlay"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmationText('');
              }}
            >
              <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Confirm Account Deletion</h3>
                </div>
                <div className="modal-body">
                  <p>
                    <strong>Are you sure you want to delete your account?</strong>
                  </p>
                  <p>
                    Your account will be deactivated immediately and scheduled for permanent
                    deletion in 7 days. During this period you will not be able to log in.
                  </p>
                  <p>
                    <strong>This action cannot be undone.</strong>
                  </p>
                  <div className="delete-account-confirm-input">
                    <label htmlFor="delete-confirm-input">
                      Type <strong>DELETE</strong> to confirm:
                    </label>
                    <input
                      type="text"
                      id="delete-confirm-input"
                      value={deleteConfirmationText}
                      onChange={(e) => setDeleteConfirmationText(e.target.value)}
                      placeholder="Type DELETE"
                      className="delete-confirm-text-input"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="modal-button-secondary"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmationText('');
                    }}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="modal-button-danger"
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || deleteConfirmationText !== 'DELETE'}
                  >
                    {deleteLoading ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </section>
      );
    }
    if (activeMenu === 'contact-us') {
      return (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>Contact Us</h2>
            </div>
          </header>
          <div className="profile-card-body">
            <p>
              You can reach us at{' '}
              <a href="mailto:contactus@zivohealth.ai">contactus@zivohealth.ai</a>.
            </p>
          </div>
        </section>
      );
    }
    if (activeMenu === 'about-zivo') {
      const appVersion = process.env.REACT_APP_VERSION || '0.1.3';
      const privacyPolicyUrl = process.env.REACT_APP_PRIVACY_POLICY_URL || 'https://zivohealth.ai/privacy';
      const termsUrl = process.env.REACT_APP_TERMS_URL || 'https://zivohealth.ai/terms';
      return (
        <section className="profile-section-card">
          <header className="profile-card-header">
            <div className="profile-card-title-row">
              <h2>About Zivo</h2>
            </div>
          </header>
          <div className="profile-card-body">
            <p>ZivoHealth – Doctor Portal</p>
            <div className="about-zivo-meta">
              <span className="about-zivo-version">Version {appVersion}</span>
            </div>
            <div className="about-zivo-links">
              <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              <span className="about-zivo-sep"> · </span>
              <a href={termsUrl} target="_blank" rel="noopener noreferrer">
                Terms &amp; Conditions
              </a>
            </div>
          </div>
        </section>
      );
    }
    return null;
  };

  return (
    <div className="dashboard">
      <DashboardHeader
        onProfileClick={() => {
          // Already on profile – keep for consistency
        }}
        onLogout={handleLogout}
      />
      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />
        <div className="dashboard-container">
          <main className="dashboard-main-content">
            {renderRightPane()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;


